// App.js
import React, { useEffect, useMemo, useState } from "react";
import {
  AppProvider,
  Page,
  Layout,
  Card,
  Text,
  InlineStack,
  BlockStack,
  Icon,
  Banner,
  Checkbox,
  Tooltip as PolarisTooltip,
  Button,
  Modal,
  Select
} from "@shopify/polaris";
import { ChartVerticalIcon, ExternalIcon, DeleteIcon } from "@shopify/polaris-icons";
import axios from "axios";
import {
  ComposedChart,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceArea,
  ReferenceLine,
  Bar
} from "recharts";
import './App.css';
import glossaryTerms from "./glossaryTerms.json";
import PerformanceDebugDashboard from "./PerformanceDebugDashboard";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { v4 as uuidv4 } from "uuid";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL || "",
  validateStatus: (status) => status >= 200 && status < 400
});

const TODAY_SALES_REFRESH_INTERVAL_SECONDS = 60;
const currentDashboardMonthDate = new Date();
const currentDashboardMonthValue = currentDashboardMonthDate.toLocaleDateString("en-CA").slice(0, 7);
const historicalDashboardMonthOptions = [
  { label: "January 2026", value: "2026-01" },
  { label: "February 2026", value: "2026-02" },
  { label: "March 2026", value: "2026-03" },
  { label: "April 2026", value: "2026-04" }
];
const DASHBOARD_MONTH_OPTIONS = historicalDashboardMonthOptions.some((option) => option.value === currentDashboardMonthValue)
  ? historicalDashboardMonthOptions
  : [
    ...historicalDashboardMonthOptions,
    {
      label: currentDashboardMonthDate.toLocaleString("default", { month: "long", year: "numeric" }),
      value: currentDashboardMonthValue
    }
  ];
const TRAFFIC_SERIES_CONFIG = {
  direct: { label: "Direct Sessions", color: "#5c6ac4" },
  organic: { label: "Organic Sessions", color: "#008060" },
  paid: { label: "Paid Sessions", color: "#b98900" }
};
const WEBOPS_STATUS_STORAGE_KEY = "dashboard.webops.statuses.v1";
const WEBOPS_ITEMS = [
  {
    key: "websiteAvailability",
    title: "Website Availability",
    definition: "Site is online, responsive, SSL healthy, and product pages load correctly."
  },
  {
    key: "checkoutRevenueFlow",
    title: "Checkout / Revenue Flow",
    definition: "Customers can add to cart, calculate tax/shipping, and complete payment."
  },
  {
    key: "erpOrderSync",
    title: "ERP Order Sync",
    definition: "Shopify orders are successfully reaching NetSuite without backlog or failures."
  },
  {
    key: "inventoryAccuracy",
    title: "Inventory Accuracy",
    definition: "Product availability, variants, and sellable quantities look trustworthy."
  },
  {
    key: "shippingIntegration",
    title: "Shipping Integration",
    definition: "ShipHawk/rates/tracking/fulfillment handoff are working."
  },
  {
    key: "trafficHealth",
    title: "Traffic Health",
    definition: "Sessions by channel are within expected range."
  },
  {
    key: "conversionHealth",
    title: "Conversion Health",
    definition: "Visitors are turning into orders at expected rate."
  },
  {
    key: "customerCommunication",
    title: "Customer Communication",
    definition: "Order confirmations, shipping emails, forms, and support contact paths are working."
  }
];
const DEFAULT_WEBOPS_STATUSES = {
  websiteAvailability: "green",
  checkoutRevenueFlow: "green",
  erpOrderSync: "green",
  inventoryAccuracy: "yellow",
  shippingIntegration: "green",
  trafficHealth: "yellow",
  conversionHealth: "green",
  customerCommunication: "green"
};

function getInitialDashboardMonth() {
  const params = new URLSearchParams(window.location.search || "");
  const requestedMonth = params.get("month");
  if (DASHBOARD_MONTH_OPTIONS.some((option) => option.value === requestedMonth)) {
    return requestedMonth;
  }

  return DASHBOARD_MONTH_OPTIONS.some((option) => option.value === currentDashboardMonthValue)
    ? currentDashboardMonthValue
    : DASHBOARD_MONTH_OPTIONS[DASHBOARD_MONTH_OPTIONS.length - 1].value;
}

function parseDashboardMonth(monthValue) {
  const [year, month] = String(monthValue || "").split("-").map(Number);
  if (!year || !month) {
    return new Date();
  }

  return new Date(year, month - 1, 1);
}

function parseDashboardDate(dateValue) {
  const [year, month, day] = String(dateValue || "").split("-").map(Number);
  if (!year || !month || !day) {
    return new Date();
  }

  return new Date(year, month - 1, day);
}

function formatDashboardMonth(dateValue) {
  const year = dateValue.getFullYear();
  const month = String(dateValue.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
}








export default function App() {
  const [loading, setLoading] = useState(true);
  const [storeConnected, setStoreConnected] = useState(true);
  const [salesYesterday, setSalesYesterday] = useState(0);
  const [refundsYesterday, setRefundsYesterday] = useState(0);
  const [onlineStoreYesterday, setOnlineStoreYesterday] = useState(0);
  const [posYesterday, setPosYesterday] = useState(0);
  const [salesMTD, setSalesMTD] = useState(0);
  const [salesMTDWithPOS, setSalesMTDWithPOS] = useState(0);
  const [monthlyGoal, setMonthlyGoal] = useState(0);
  const [planMTD, setPlanMTD] = useState(0);
  const [percentToPlan, setPercentToPlan] = useState(0);
  const [percentToPlanWithPOS, setPercentToPlanWithPOS] = useState(0);
  const [orders, setOrders] = useState([]);
  const [environment, setEnvironment] = useState("unknown");
  const [chartData, setChartData] = useState([]);
  const [trafficCharts, setTrafficCharts] = useState({ direct: [], organic: [], paid: [] });
  const [trafficSummary, setTrafficSummary] = useState({});
  const [trafficSignals, setTrafficSignals] = useState({});
  const [topReferringChannels, setTopReferringChannels] = useState({ direct: [], organic: [], paid: [] });
  const [trafficComparisonWindow, setTrafficComparisonWindow] = useState(null);
  const [trafficLoading, setTrafficLoading] = useState(true);
  const [trafficError, setTrafficError] = useState("");
  const [salesSummary, setSalesSummary] = useState("");
  const [salesSummaryBullets, setSalesSummaryBullets] = useState([]);
  const [salesCollapsed, setSalesCollapsed] = useState(false);
  const [topProductsMTD, setTopProductsMTD] = useState([]);
  const [showWebTrendline, setShowWebTrendline] = useState(true);
  const [showRealitySeasoning, setShowRealitySeasoning] = useState(false);
  const [showSimulation, setShowSimulation] = useState(false);
  const [compareGoalModel, setCompareGoalModel] = useState(false);
  const [webScenarioKeys, setWebScenarioKeys] = useState([]);
  const [dashboardChartSource, setDashboardChartSource] = useState(null);
  const [pacingDiagnosticsV2, setPacingDiagnosticsV2] = useState(null);
  const [majorProjects, setMajorProjects] = useState([]);
  const [projectsDirty, setProjectsDirty] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved
  const [dotCount, setDotCount] = useState(0); // For "Saving..."
  const [selectedMonth, setSelectedMonth] = useState(getInitialDashboardMonth);
  const selectedMonthDate = parseDashboardMonth(selectedMonth);
  const selectedMonthLabel = selectedMonthDate.toLocaleString("default", { month: "long", year: "numeric" });
  const monthName = selectedMonthDate.toLocaleString("default", { month: "long" });
  const monthNameAbbr = selectedMonthDate.toLocaleString("default", { month: "short" });
  const currentMonthKey = new Date().toLocaleDateString("en-CA").slice(0, 7);
  const isSelectedMonthActive = selectedMonth === currentMonthKey;
  const [showCompleted, setShowCompleted] = useState(false);
  const [todaysSales, setTodaysSales] = useState(0);
  const [todaysGoal, setTodaysGoal] = useState(0);
  const [goalYesterday, setGoalYesterday] = useState(0);
  const [ordersCollapsed, setOrdersCollapsed] = useState(true);
  const [yearlyStats, setYearlyStats] = useState(null);
  const [intradayPacing, setIntradayPacing] = useState(null);
  const [glossaryTermsMap, setGlossaryTermsMap] = useState(glossaryTerms);
  const [glossaryModalOpen, setGlossaryModalOpen] = useState(false);
  const [glossaryDraft, setGlossaryDraft] = useState([]);
  const [glossarySaving, setGlossarySaving] = useState(false);
  const [simulationModalOpen, setSimulationModalOpen] = useState(false);
  const [simulationDraft, setSimulationDraft] = useState([{ id: uuidv4(), amount: "" }]);
  const [refreshCountdown, setRefreshCountdown] = useState(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isReevaluating, setIsReevaluating] = useState(false);
  const [hoveredGoalSeries, setHoveredGoalSeries] = useState(null);
  const [showPerfDashboard, setShowPerfDashboard] = useState(false);
  const [movingAveragePreset, setMovingAveragePreset] = useState(() => {
    const configured = Math.max(1, Math.floor(Number(process.env.REACT_APP_DAILY_MA_DAYS) || 5));
    if (configured === 3) return "3";
    if (configured === 5) return "5";
    if (configured === 7) return "7";
    if (configured === 14) return "14";
    return "custom";
  });
  const [movingAverageCustomDaysInput, setMovingAverageCustomDaysInput] = useState(() => {
    const configured = Number(process.env.REACT_APP_DAILY_MA_DAYS);
    if (!Number.isFinite(configured)) return "5";
    return String(Math.max(1, Math.floor(configured)));
  });
  const [webopsStatuses, setWebopsStatuses] = useState(() => {
    if (typeof window === "undefined") {
      return DEFAULT_WEBOPS_STATUSES;
    }

    try {
      const stored = window.localStorage.getItem(WEBOPS_STATUS_STORAGE_KEY);
      if (!stored) {
        return DEFAULT_WEBOPS_STATUSES;
      }

      const parsed = JSON.parse(stored);
      return {
        ...DEFAULT_WEBOPS_STATUSES,
        ...(parsed || {})
      };
    } catch (err) {
      console.warn("Unable to restore WebOps statuses from localStorage.", err);
      return DEFAULT_WEBOPS_STATUSES;
    }
  });

  useEffect(() => {
    const path = window.location.pathname || "";
    const search = window.location.search || "";
    const isPerfPath = path.toLowerCase().includes("/performance");
    const isPerfQuery = /(?:[?&])(perf|debug)=perf/.test(search.toLowerCase());
    if (isPerfPath || isPerfQuery) {
      setShowPerfDashboard(true);
    }
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(WEBOPS_STATUS_STORAGE_KEY, JSON.stringify(webopsStatuses));
  }, [webopsStatuses]);

  const handleDeleteProject = async (id) => {


    try {
      await api.delete(`/api/projects/${id}`);
      setMajorProjects(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Failed to delete project:", err);
    }
  };

  // 👇 PUT IT HERE (with the others)
  useEffect(() => {
    console.log("STATE UPDATED:", majorProjects);
  }, [majorProjects]);

  const markProjectsDirty = () => {
    setProjectsDirty(true);
    setSaveStatus((current) => (current === "saving" ? current : "idle"));
  };

  const buildProjectsPayload = () => (
    majorProjects.map((p, idx) => ({
      id: p.id || null,
      tempId: p.tempId || null,
      project_name: p.project_name || "",
      completed_date: p.completed_date || null,
      sort_order: p.sort_order ?? idx
    }))
  );

  const hydrateProjects = (projects) => (
    projects
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((p) => ({
        id: p.id,
        tempId: p.temp_id || p.tempId || uuidv4(),
        project_name: p.project_name,
        completed_date: p.completed_date,
        sort_order: p.sort_order
      }))
  );

  const saveProjects = async () => {
    setSaveStatus("saving");

    try {
      const payload = buildProjectsPayload();
      await api.post("/api/projects", { projects: payload });
      const refreshed = await api.get("/api/projects");

      setMajorProjects(hydrateProjects(refreshed.data.projects));
      setProjectsDirty(false);
      setSaveStatus("saved");

      setTimeout(() => {
        setSaveStatus((current) => (current === "saved" ? "idle" : current));
      }, 2000);
    } catch (err) {
      console.error("Failed saving projects:", err);
      setSaveStatus("idle");
    }
  };

  async function refreshTodaySales() {
    if (!isSelectedMonthActive) {
      return;
    }

    try {
      const response = await api.get("/api/today-sales");

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      setTodaysSales(response.data.todaySales || 0);
      setTodaysGoal(response.data.todaysGoal || 0);
      setIntradayPacing(response.data.intradayPacing || null);
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);
      setStoreConnected(true);
    } catch (err) {
      console.error("Error refreshing today's sales:", err);
    }
  }

  function withIntelligentForecastContext(summaryText, chartRows = null) {
    const rows = Array.isArray(chartRows) && chartRows.length ? chartRows : chartData;
    if (!Array.isArray(rows) || !rows.length) {
      return summaryText || "";
    }

    return summaryText || "";
  }

  async function fetchDashboardSummary(chartRows = null, options = {}) {
    const { force = false } = options;

    try {
      const response = await api.get("/api/dashboard-summary", {
        params: {
          month: selectedMonth,
          ...(force ? { force: 1 } : {})
        }
      });

      if (response.status >= 200 && response.status < 400) {
        setSalesSummary(withIntelligentForecastContext(response.data?.summary?.text || "", chartRows));
        setSalesSummaryBullets(Array.isArray(response.data?.summary?.bullets) ? response.data.summary.bullets : []);
        return;
      }
    } catch (err) {
      console.error("Error fetching dashboard summary:", err);
    }

    if (chartRows) {
      setSalesSummary(withIntelligentForecastContext(webSalesTrendSentence(chartRows), chartRows));
      setSalesSummaryBullets([]);
    }
  }

  async function fetchGlossaryTerms() {
    try {
      const response = await api.get("/api/glossary");
      const terms = response.data?.terms;
      if (terms && typeof terms === "object") {
        setGlossaryTermsMap(terms);
      }
    } catch (err) {
      console.error("Error fetching glossary terms:", err);
    }
  }

  async function fetchTrafficData() {
    setTrafficLoading(true);
    setTrafficError("");

    try {
      const trafficMonthEnd = parseDashboardMonth(selectedMonth);
      const trafficMonthStart = new Date(trafficMonthEnd.getFullYear(), trafficMonthEnd.getMonth() - 11, 1);
      const response = await api.get("/api/traffic/charts", {
        params: {
          month_from: formatDashboardMonth(trafficMonthStart),
          month_to: selectedMonth
        }
      });
      setTrafficCharts({
        direct: Array.isArray(response.data?.direct) ? response.data.direct : [],
        organic: Array.isArray(response.data?.organic) ? response.data.organic : [],
        paid: Array.isArray(response.data?.paid) ? response.data.paid : []
      });
      setTrafficSummary(response.data?.summary || {});
      setTrafficSignals(response.data?.trafficSignals || {});
      setTopReferringChannels(response.data?.topReferringChannels || { direct: [], organic: [], paid: [] });
      setTrafficComparisonWindow(response.data?.comparisonWindow || null);
    } catch (err) {
      console.error("Error fetching traffic analytics:", err);
      setTrafficError("Unable to load traffic analytics.");
    } finally {
      setTrafficLoading(false);
    }
  }

  const buildChartWithForecasts = ({
    dailyGoals,
    todaySales,
    backendGoal,
    forecastModel
  }) => {
    let mtdActual = 0;
    let mtdGoal = 0;
    let mtdWebOnly = 0;

    const sortedGoals = (dailyGoals || []).sort(
      (a, b) => new Date(a.date) - new Date(b.date)
    );
    const daysInMonth = sortedGoals.length || 0;

    const todayISO = new Date().toLocaleDateString("en-CA");
    const simulationAmounts = showSimulation
      ? simulationDraft
        .map((entry) => Number(String(entry.amount || "").replace(/[^0-9.-]/g, "")))
        .filter((amount) => Number.isFinite(amount) && amount > 0)
      : [];

    const chartDataSafe = (sortedGoals || []).map((dg) => {
      const [year, month, day] = dg.date.split("-").map(Number);
      const dt = new Date(year, month - 1, day);
      const isToday = dg.date === todayISO;
      const web = isToday
        ? (todaySales || 0)
        : (dg.channels?.["Online Store"]?.net || 0);
      const pos = isToday
        ? 0
        : (dg.channels?.["Point of Sale"]?.net || 0);

      const total = web + pos;
      const goal = Number(dg.goal) || 0;
      const dailyPercent = goal > 0 ? (total / goal) * 100 : 0;
      const dailyWebPercent = goal > 0 ? (web / goal) * 100 : 0;
      const isFuture = dg.date > todayISO;

      mtdActual += total;
      mtdGoal += goal;
      mtdWebOnly += web;

      return {
        date: dt,
        dateISO: dg.date,
        formattedDate: dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
        goal,
        actual: total,
        webOnly: web,
        posOnly: pos,
        dailyPercentOfGoal: isFuture ? null : dailyPercent,
        dailyWebPercentOfGoal: isFuture ? null : dailyWebPercent,
        mtdPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdActual / mtdGoal) * 100 : 0),
        mtdWebPercentOfGoal: isFuture ? null : (mtdGoal > 0 ? (mtdWebOnly / mtdGoal) * 100 : 0)
      };
    });

    let runningWebPctTotal = 0;
    let runningWebPctCount = 0;
    let simulationIndex = 0;

    const finalChartData = chartDataSafe.map((d) => {
      let simulationAmount = null;
      let effectiveDailyWebPercent = d.dailyWebPercentOfGoal;

      if ((effectiveDailyWebPercent === null || effectiveDailyWebPercent === undefined) && simulationIndex < simulationAmounts.length) {
        simulationAmount = simulationAmounts[simulationIndex];
        simulationIndex += 1;
        effectiveDailyWebPercent = d.goal > 0 ? (simulationAmount / d.goal) * 100 : 0;
      }

      if (effectiveDailyWebPercent !== null && effectiveDailyWebPercent !== undefined) {
        runningWebPctTotal += effectiveDailyWebPercent;
        runningWebPctCount += 1;
      }

      return {
        ...d,
        simulatedWebOnly: simulationAmount,
        simulationDailyWebPercentOfGoal: simulationAmount !== null ? effectiveDailyWebPercent : null,
        webTrend: runningWebPctCount > 0 ? (runningWebPctTotal / runningWebPctCount) : null
      };
    });

    const actualWebDayIndexes = finalChartData
      .map((day, index) => (
        day.dailyWebPercentOfGoal !== null && day.dailyWebPercentOfGoal !== undefined
          ? index
          : null
      ))
      .filter((index) => index !== null);

    const scenarioKeys = actualWebDayIndexes
      .slice(0, -1)
      .map((_, scenarioIndex) => `webScenario${scenarioIndex + 1}`);

    const lastChartIndex = finalChartData.length - 1;
    const chartWithScenarios = finalChartData.map((day) => ({ ...day }));

    actualWebDayIndexes.slice(0, -1).forEach((startIndex, scenarioIndex) => {
      const scenarioKey = `webScenario${scenarioIndex + 1}`;
      const startValue = chartWithScenarios[startIndex].dailyWebPercentOfGoal;
      const projectedFinish = chartWithScenarios[startIndex].webTrend;
      const remainingPoints = Math.max(lastChartIndex - startIndex, 1);

      for (let pointIndex = startIndex; pointIndex <= lastChartIndex; pointIndex += 1) {
        const progress = (pointIndex - startIndex) / remainingPoints;
        const easedProgress = 1 - ((1 - progress) ** 2);

        chartWithScenarios[pointIndex][scenarioKey] = startValue + (
          (projectedFinish - startValue) * easedProgress
        );
      }
    });

    const simulationTotal = simulationAmounts.reduce((sum, amount) => sum + amount, 0);
    const futureChartDays = finalChartData.filter((day) => day.dailyWebPercentOfGoal === null || day.dailyWebPercentOfGoal === undefined);
    const coreDailyRunRate = Number(
      forecastModel?.debug?.weightedRecentRunRate
      || forecastModel?.debug?.coreDailyRunRate
      || 0
    );
    const simulatedEventContribution = {
      outperform: 0,
      breakout: 0,
      extreme: 0
    };

    let simulationCoreDelta = 0;
    simulationAmounts.forEach((amount, index) => {
      const futureDay = futureChartDays[index];
      const simulatedGoal = Number(futureDay?.goal || 0);
      const dailyGoalPct = simulatedGoal > 0 ? (amount / simulatedGoal) * 100 : 0;
      const monthlyGoalPct = backendGoal > 0 ? (amount / backendGoal) * 100 : 0;

      let simulationClass = "core";
      if (dailyGoalPct >= Number(forecastModel?.thresholds?.extreme?.dailyGoalPctMin || 0) &&
        monthlyGoalPct >= Number(forecastModel?.thresholds?.extreme?.monthlyGoalPctMin || 0)) {
        simulationClass = "extreme";
      } else if (dailyGoalPct >= Number(forecastModel?.thresholds?.breakout?.dailyGoalPctMin || 0) &&
        monthlyGoalPct >= Number(forecastModel?.thresholds?.breakout?.monthlyGoalPctMin || 0)) {
        simulationClass = "breakout";
      } else if (dailyGoalPct >= Number(forecastModel?.thresholds?.outperform?.dailyGoalPctMin || 0)) {
        simulationClass = "outperform";
      }

      simulationCoreDelta += amount - coreDailyRunRate;

      if (simulationClass !== "core") {
        simulatedEventContribution[simulationClass] += Math.max(amount - simulatedGoal, 0);
      }
    });

    const coreForecast = Number(forecastModel?.coreForecast || 0) + simulationCoreDelta;
    const remainingOutperformForecast = Math.max(
      0,
      Number(forecastModel?.outperformForecast || 0) - simulatedEventContribution.outperform
    );
    const remainingBreakoutForecast = Math.max(
      0,
      Number(forecastModel?.breakoutForecast || 0) - simulatedEventContribution.breakout
    );
    const remainingExtremeForecast = Math.max(
      0,
      Number(forecastModel?.extremeForecast || 0) - simulatedEventContribution.extreme
    );
    const intelligentForecastTarget = coreForecast + remainingOutperformForecast + remainingBreakoutForecast + remainingExtremeForecast;
    const realitySeasoningPct = backendGoal > 0 && intelligentForecastTarget > 0
      ? (intelligentForecastTarget / backendGoal) * 100
      : null;
    const lastActualWebIndex = actualWebDayIndexes.length
      ? actualWebDayIndexes[actualWebDayIndexes.length - 1]
      : -1;
    const baseForecastPct = chartWithScenarios.length
      ? Number(chartWithScenarios[chartWithScenarios.length - 1]?.webTrend || 0)
      : 0;
    const realitySeasoningStartPct = lastActualWebIndex >= 0
      ? Number(chartWithScenarios[lastActualWebIndex]?.webTrend ?? chartWithScenarios[lastActualWebIndex]?.dailyWebPercentOfGoal ?? baseForecastPct)
      : baseForecastPct;
    const remainingRealityPoints = Math.max(lastChartIndex - lastActualWebIndex, 1);

    if (realitySeasoningPct !== null && Number.isFinite(realitySeasoningPct) && lastActualWebIndex >= 0) {
      for (let pointIndex = lastActualWebIndex; pointIndex <= lastChartIndex; pointIndex += 1) {
        const progress = (pointIndex - lastActualWebIndex) / remainingRealityPoints;
        chartWithScenarios[pointIndex].realitySeasoning = realitySeasoningStartPct + (
          (realitySeasoningPct - realitySeasoningStartPct) * progress
        );
      }
    }

    if (showRealitySeasoning) {
      console.log("Reality seasoning debug", {
        backendGoal,
        simulationTotal,
        simulationCoreDelta,
        simulatedEventContribution,
        coreForecast,
        remainingOutperformForecast,
        remainingBreakoutForecast,
        remainingExtremeForecast,
        intelligentForecastTarget,
        backendBaseForecast: Number(forecastModel?.baseForecast || 0)
      });
    }

    return {
      chartWithScenarios,
      scenarioKeys
    };
  };

  async function fetchDashboardData(options = {}) {
    const { showLoadingSpinner = false, todayOnly = false } = options;

    if (showLoadingSpinner) {
      setLoading(true);
    }

    try {
      const response = await api.get("/api/dashboard", {
        params: { month: selectedMonth }
      });

      if (response.status === 401) {
        setStoreConnected(false);
        return;
      }

      const {
        environment: env,
        isActiveMonth: responseIsActiveMonth,
        periodLastCompletedDate,
        yesterday,
        refunds,
        monthToDate,
        monthToDateWithPOS,
        yesterdayOrders,
        monthlyGoal: backendGoal,
        onlineStoreYesterday: onlineYesterday,
        posYesterday: posYest,
        topProductsMTD: topProductsFromBackend,
        dailyGoals,
        yearly,
        forecastModel,
        pacingDiagnosticsV2: pacingDiagnosticsFromBackend,
        todaySales,
        intradayPacing: intradayPacingFromBackend
      } = response.data;

      setTodaysSales(responseIsActiveMonth ? (todaySales || 0) : (yesterday || 0));
      setRefreshCountdown(TODAY_SALES_REFRESH_INTERVAL_SECONDS);

      if (todayOnly) {
        setStoreConnected(true);
        return;
      }

      const sortedGoals = (dailyGoals || []).sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      const selectedPeriodISO = periodLastCompletedDate || sortedGoals[sortedGoals.length - 1]?.date || new Date().toLocaleDateString("en-CA");
      const selectedPeriodDate = parseDashboardDate(selectedPeriodISO);
      const selectedDayISO = responseIsActiveMonth
        ? new Date().toLocaleDateString("en-CA")
        : selectedPeriodISO;
      const plannedThroughYesterday = sortedGoals
        .filter((entry) => String(entry?.date || "") <= selectedPeriodISO)
        .reduce((sum, entry) => sum + (Number(entry?.goal) || 0), 0);

      setSalesYesterday(yesterday || 0);
      setEnvironment(env);
      setRefundsYesterday(refunds || 0);
      setSalesMTD(monthToDate || 0);
      setSalesMTDWithPOS(monthToDateWithPOS || 0);
      setMonthlyGoal(backendGoal || 0);
      setOrders(yesterdayOrders || []);
      setOnlineStoreYesterday(onlineYesterday || 0);
      setPosYesterday(posYest || 0);
      setPlanMTD(plannedThroughYesterday);
      setYearlyStats(yearly || null);
      setIntradayPacing(intradayPacingFromBackend || null);
      setPacingDiagnosticsV2(pacingDiagnosticsFromBackend || null);

      setPercentToPlan(
        plannedThroughYesterday > 0
          ? ((monthToDate / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setPercentToPlanWithPOS(
        plannedThroughYesterday > 0
          ? ((monthToDateWithPOS / plannedThroughYesterday) * 100).toFixed(1)
          : 0
      );

      setStoreConnected(true);
      setTopProductsMTD(topProductsFromBackend || []);

      const todayGoalRow = sortedGoals.find((g) => g.date === selectedDayISO);
      const yesterdayISO = selectedPeriodDate.toLocaleDateString("en-CA");
      const yesterdayGoalRow = sortedGoals.find((g) => g.date === yesterdayISO);
      const todayGoalValue = Number(todayGoalRow?.goal) || 0;
      const yesterdayGoalValue = Number(yesterdayGoalRow?.goal) || 0;
      setTodaysGoal(todayGoalValue);
      setGoalYesterday(yesterdayGoalValue);
      const chartSource = {
        dailyGoals,
        todaySales,
        backendGoal,
        plannedThroughYesterday,
        monthToDate,
        forecastModel
      };
      setDashboardChartSource(chartSource);
      const { chartWithScenarios, scenarioKeys } = buildChartWithForecasts(chartSource);
      setWebScenarioKeys(scenarioKeys);
      setChartData(chartWithScenarios);
      await fetchDashboardSummary(chartWithScenarios);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setStoreConnected(false);
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  }

  useEffect(() => {
    fetchDashboardData({ showLoadingSpinner: true });
    fetchGlossaryTerms();
    fetchTrafficData();
  }, [selectedMonth]);

  useEffect(() => {
    if (!dashboardChartSource) {
      return;
    }

    const { chartWithScenarios, scenarioKeys } = buildChartWithForecasts(dashboardChartSource);
    setWebScenarioKeys(scenarioKeys);
    setChartData(chartWithScenarios);
  }, [dashboardChartSource, showSimulation, simulationDraft]);

  useEffect(() => {
    if (!isSelectedMonthActive) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      refreshTodaySales();
    }, TODAY_SALES_REFRESH_INTERVAL_SECONDS * 1000);

    return () => clearInterval(intervalId);
  }, [isSelectedMonthActive, selectedMonth]);

  useEffect(() => {
    const countdownId = setInterval(() => {
      setRefreshCountdown(prev =>
        prev > 0 ? prev - 1 : 0
      );
    }, 1000);

    return () => clearInterval(countdownId);
  }, []);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const res = await api.get("/api/projects");
        setMajorProjects(hydrateProjects(res.data.projects));
        setProjectsDirty(false);
      } catch (err) {
        console.error(err);
      }
    }
    fetchProjects();
  }, []);

  useEffect(() => {
    if (saveStatus !== "saving") {
      setDotCount(0);
      return; // no interval needed
    }

    const intervalId = setInterval(() => {
      setDotCount(prev => (prev + 1) % 4); // cycles 0 → 3 dots
    }, 500);

    return () => clearInterval(intervalId); // cleanup
  }, [saveStatus]);

  const formatCurrency = (num) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num || 0);

  const formatSimulationAmount = (value) => {
    const rawValue = String(value ?? "");
    if (!rawValue) {
      return "";
    }

    const sanitized = rawValue.replace(/[^0-9.]/g, "");
    if (!sanitized) {
      return "";
    }

    const [wholePartRaw, decimalPartRaw] = sanitized.split(".");
    const wholePart = wholePartRaw ? Number(wholePartRaw).toLocaleString("en-US") : "0";

    if (sanitized.endsWith(".") && decimalPartRaw === undefined) {
      return `${wholePart}.`;
    }

    if (decimalPartRaw !== undefined) {
      return `${wholePart}.${decimalPartRaw.slice(0, 2)}`;
    }

    return wholePart;
  };

  const refreshLabel = isSelectedMonthActive
    ? `Rechecking sales in (${refreshCountdown}s)`
    : "Refresh dashboard";

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        fetchDashboardData(),
        fetchTrafficData()
      ]);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSelectedMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
    setSalesSummary("");
    setSalesSummaryBullets([]);
    setDashboardChartSource(null);
    setChartData([]);
    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set("month", newMonth);
    window.history.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}`);
  };

  if (showPerfDashboard) {
    return (
      <AppProvider>
        <PerformanceDebugDashboard api={api} environment={environment} />
      </AppProvider>
    );
  }

  const handleReevaluate = async () => {
    setIsReevaluating(true);
    try {
      await fetchDashboardSummary(chartData, { force: true });
    } finally {
      setIsReevaluating(false);
    }
  };

  const openGlossaryModal = () => {
    const entries = Object.entries(glossaryTermsMap || {})
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([term, definition]) => ({ id: uuidv4(), term, definition }));
    setGlossaryDraft(entries);
    setGlossaryModalOpen(true);
  };

  const handleGlossaryDraftChange = (index, field, value) => {
    setGlossaryDraft((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, [field]: value } : entry
    )));
  };

  const addGlossaryDraftRow = () => {
    setGlossaryDraft((current) => [...current, { id: uuidv4(), term: "", definition: "" }]);
  };

  const removeGlossaryDraftRow = (index) => {
    setGlossaryDraft((current) => current.filter((_, entryIndex) => entryIndex !== index));
  };

  const saveGlossary = async () => {
    setGlossarySaving(true);
    try {
      const terms = glossaryDraft.reduce((acc, entry) => {
        const term = String(entry.term || "").trim().toLowerCase();
        const definition = String(entry.definition || "").trim();
        if (term && definition) {
          acc[term] = definition;
        }
        return acc;
      }, {});

      const response = await api.post("/api/glossary", { terms });
      const savedTerms = response.data?.terms || terms;
      setGlossaryTermsMap(savedTerms);
      setGlossaryModalOpen(false);
    } catch (err) {
      console.error("Error saving glossary terms:", err);
    } finally {
      setGlossarySaving(false);
    }
  };

  const openSimulationModal = () => {
    setSimulationModalOpen(true);
  };

  const closeSimulationModal = () => {
    setSimulationModalOpen(false);
  };

  const handleSimulationToggle = (newValue) => {
    setShowSimulation(newValue);

    if (newValue) {
      openSimulationModal();
      return;
    }

    setSimulationModalOpen(false);
  };

  const addSimulationDraftRow = () => {
    setSimulationDraft((current) => [...current, { id: uuidv4(), amount: "" }]);
  };

  const handleSimulationDraftChange = (index, value) => {
    const sanitizedValue = String(value || "").replace(/[^0-9.]/g, "");

    setSimulationDraft((current) => current.map((entry, entryIndex) => (
      entryIndex === index ? { ...entry, amount: sanitizedValue } : entry
    )));
  };

  const removeSimulationDraftRow = (index) => {
    setSimulationDraft((current) => {
      const nextRows = current.filter((_, entryIndex) => entryIndex !== index);
      return nextRows.length ? nextRows : [{ id: uuidv4(), amount: "" }];
    });
  };

  const tone = percentToPlan >= 100 ? "success" : percentToPlan >= 80 ? "caution" : "critical";
  const mtdTone = percentToPlanWithPOS >= 100 ? "success" : percentToPlanWithPOS >= 80 ? "caution" : "critical";
  const selectedDaySalesLabel = isSelectedMonthActive ? "Today's Net Sales" : "Final Day Net Sales";
  const selectedDayGoalLabel = isSelectedMonthActive ? "Today's Goal" : "Final Day Goal";
  const selectedDayPaceLabel = isSelectedMonthActive ? "Intraday Forcast" : "Final Day Pace";
  const getKey = (p) => p.id ?? p.tempId;
  const now = new Date();
  const currentMonthDays = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  const todayISO = now.toLocaleDateString("en-CA");
  const isTodayComplete = now.getHours() >= 23 && now.getMinutes() >= 55;
  const goalRows = Array.isArray(dashboardChartSource?.dailyGoals) ? dashboardChartSource.dailyGoals : [];
  const sortedGoalRows = [...goalRows].sort((a, b) => String(a.date).localeCompare(String(b.date)));
  const daysInMonthForForecast = sortedGoalRows.length || currentMonthDays;
  const completedDayRows = sortedGoalRows.filter((row) => (
    String(row?.date) < todayISO || (String(row?.date) === todayISO && isTodayComplete)
  ));
  const completedDaySales = completedDayRows.map((row) => Number(row?.channels?.["Online Store"]?.net || 0));
  const recentCompletedDaySales = completedDaySales.length >= 7
    ? completedDaySales.slice(-7)
    : completedDaySales;
  const completedDaysUsed = recentCompletedDaySales.length;
  const recentCompletedDaySalesTotal = recentCompletedDaySales.reduce((sum, value) => sum + value, 0);
  const recentCompletedDayRunRate = recentCompletedDaySales.length
    ? (recentCompletedDaySalesTotal / recentCompletedDaySales.length)
    : 0;
  const completedDaysCount = completedDayRows.length;
  const remainingDays = Math.max(daysInMonthForForecast - completedDaysCount, 0);
  const forecastRemainingCoreSales = recentCompletedDayRunRate * remainingDays;
  const simpleForecast = planMTD > 0
    ? monthlyGoal * (salesMTD / planMTD)
    : salesMTD;
  const expectedOutperformContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.outperformForecast || 0));
  const expectedBreakoutContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.breakoutForecast || 0));
  const expectedExtremeContribution = Math.max(0, Number(dashboardChartSource?.forecastModel?.extremeForecast || 0));
  const remainingExpectedOutlierCount = (
    Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.outperform || 0)
    + Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.breakout || 0)
    + Number(dashboardChartSource?.forecastModel?.remainingExpectedDays?.extreme || 0)
  );
  const expectedRemainingOutlierUpside = expectedOutperformContribution + expectedBreakoutContribution + expectedExtremeContribution;
  const intelligentForecast = salesMTD + forecastRemainingCoreSales + expectedRemainingOutlierUpside;
  const intelligentForecastValue = Math.max(salesMTD, intelligentForecast);
  const chartIntelligentForecastPct = chartData.length
    ? Number(chartData[chartData.length - 1]?.realitySeasoning || 0)
    : 0;
  const monthSalesProjectedValue = Math.max(salesMTD, simpleForecast);
  const intelligentForecastPct = monthlyGoal > 0
    ? (intelligentForecastValue / monthlyGoal) * 100
    : 0;
  const trafficSummaryText = buildTrafficSummarySentence(trafficSignals, trafficSummary);
  const projectedPanelTone = intelligentForecastPct >= 100
    ? {
      value: "#1b5e20"
    }
    : intelligentForecastPct >= 80
      ? {
        value: "#8d6e00"
      }
      : {
        value: "#b71c1c"
      };
  const intradayPacePct = Number(intradayPacing?.pacePct || 0);
  const intradayExpectedSales = Number(intradayPacing?.expectedSalesByNow || 0);
  const intradayExpectedPct = Number(intradayPacing?.expectedPctByNow || 0);
  const intradayTone = intradayPacing
    ? (intradayPacePct >= 100 ? "success" : intradayPacePct >= 85 ? "caution" : "critical")
    : tone;

  const goalModelDiagnostics = useMemo(() => {
    if (!Array.isArray(chartData) || !chartData.length) {
      return { rows: chartData || [], stats: null };
    }

    const monthGoal = chartData.reduce((sum, row) => sum + (Number(row?.goal) || 0), 0);
    const todayIso = new Date().toLocaleDateString("en-CA");
    const completedRows = chartData.filter((row) => String(row?.dateISO) < todayIso);
    const baseDailyGoal = chartData.length > 0 ? (monthGoal / chartData.length) : 0;

    const isOutlierDay = (row) => {
      const goal = Number(row?.goal || 0);
      const actual = Number(row?.webOnly || 0);
      const dailyPct = goal > 0 ? (actual / goal) * 100 : 0;
      const monthlyPct = monthGoal > 0 ? (actual / monthGoal) * 100 : 0;
      return dailyPct >= 200 && monthlyPct >= 5;
    };

    const coreRows = completedRows.filter((row) => !isOutlierDay(row));
    const sourceRows = coreRows.length >= 5 ? coreRows : completedRows;

    const weekdayRatios = new Map();
    sourceRows.forEach((row) => {
      const weekday = new Date(row.date).getDay();
      const actual = Number(row?.webOnly || 0);
      const ratioRaw = baseDailyGoal > 0 ? (actual / baseDailyGoal) : 0;
      const ratioCapped = Math.max(0.15, Math.min(ratioRaw, 2.5));
      if (!weekdayRatios.has(weekday)) weekdayRatios.set(weekday, []);
      weekdayRatios.get(weekday).push(ratioCapped);
    });

    const fallbackShares = [0.03, 0.16, 0.15, 0.15, 0.23, 0.16, 0.12];
    const weekdayScores = Array.from({ length: 7 }, (_, weekday) => {
      const values = weekdayRatios.get(weekday) || [];
      if (!values.length) return fallbackShares[weekday] * 7;
      const sorted = [...values].sort((a, b) => a - b);
      const middle = Math.floor(sorted.length / 2);
      return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
    });

    const totalScore = weekdayScores.reduce((sum, score) => sum + score, 0);
    const normalizedWeekShare = weekdayScores.map((score, index) => (
      totalScore > 0 ? (score / totalScore) : fallbackShares[index]
    ));

    const weekdayCountInMonth = chartData.reduce((acc, row) => {
      const weekday = new Date(row.date).getDay();
      acc[weekday] = (acc[weekday] || 0) + 1;
      return acc;
    }, {});

    const recalibratedGoalsRaw = chartData.map((row) => {
      const weekday = new Date(row.date).getDay();
      const count = Number(weekdayCountInMonth[weekday] || 1);
      const weekdayBudget = monthGoal * normalizedWeekShare[weekday];
      return Math.round(weekdayBudget / count);
    });
    const rawRollup = recalibratedGoalsRaw.reduce((sum, value) => sum + value, 0);
    const rollupDelta = Math.round(monthGoal - rawRollup);
    if (recalibratedGoalsRaw.length > 0 && rollupDelta !== 0) {
      recalibratedGoalsRaw[recalibratedGoalsRaw.length - 1] += rollupDelta;
    }

    const rows = chartData.map((row, index) => {
      const actual = Number(row?.webOnly || 0);
      const oldGoal = Number(row?.goal || 0);
      const recalGoal = Number(recalibratedGoalsRaw[index] || 0);
      const currentPct = oldGoal > 0 ? (actual / oldGoal) * 100 : null;
      const recalPct = recalGoal > 0 ? (actual / recalGoal) * 100 : null;
      return {
        ...row,
        oldDailyGoal: oldGoal,
        recalibratedDailyGoal: recalGoal,
        currentDailyGoalPct: currentPct,
        recalibratedDailyGoalPct: recalPct,
        varianceVsCurrentGoalPct: currentPct !== null ? currentPct - 100 : null,
        varianceVsRecalibratedGoalPct: recalPct !== null ? recalPct - 100 : null
      };
    });

    const completedRowsWithDiag = rows.filter((row) => String(row?.dateISO) < todayIso);
    const varianceCurrent = completedRowsWithDiag
      .map((row) => Math.abs(Number(row.varianceVsCurrentGoalPct)))
      .filter((value) => Number.isFinite(value));
    const varianceRecal = completedRowsWithDiag
      .map((row) => Math.abs(Number(row.varianceVsRecalibratedGoalPct)))
      .filter((value) => Number.isFinite(value));
    const daysOverOld = completedRowsWithDiag.filter((row) => Number(row.currentDailyGoalPct) >= 100).length;
    const daysOverRecal = completedRowsWithDiag.filter((row) => Number(row.recalibratedDailyGoalPct) >= 100).length;
    const biggestOverCurrent = completedRowsWithDiag
      .slice()
      .sort((a, b) => Number(b.varianceVsCurrentGoalPct) - Number(a.varianceVsCurrentGoalPct))[0] || null;
    const biggestUnderCurrent = completedRowsWithDiag
      .slice()
      .sort((a, b) => Number(a.varianceVsCurrentGoalPct) - Number(b.varianceVsCurrentGoalPct))[0] || null;

    return {
      rows,
      stats: {
        avgAbsVarianceOld: varianceCurrent.length ? (varianceCurrent.reduce((a, b) => a + b, 0) / varianceCurrent.length) : 0,
        avgAbsVarianceRecal: varianceRecal.length ? (varianceRecal.reduce((a, b) => a + b, 0) / varianceRecal.length) : 0,
        daysOverOld,
        daysOverRecal,
        biggestOverCurrent,
        biggestUnderCurrent
      }
    };
  }, [chartData]);

  const revenueGoalDiagnostics = useMemo(() => {
    const rows = Array.isArray(goalModelDiagnostics.rows) ? goalModelDiagnostics.rows : [];
    let runningActualMtd = 0;
    let runningCurrentExpectedMtd = 0;
    let runningRecalExpectedMtd = 0;

    const enrichedRows = rows.map((row) => {
      const actualRevenue = Number(row?.webOnly || 0);
      const currentExpected = Number(row?.oldDailyGoal || row?.goal || 0);
      const recalibratedExpected = Number(row?.recalibratedDailyGoal || row?.goal || 0);

      runningActualMtd += actualRevenue;
      runningCurrentExpectedMtd += currentExpected;
      runningRecalExpectedMtd += recalibratedExpected;

      return {
        ...row,
        actualRevenue,
        currentExpectedRevenue: currentExpected,
        recalibratedExpectedRevenue: recalibratedExpected,
        actualMTD: runningActualMtd,
        currentExpectedMTD: runningCurrentExpectedMtd,
        recalibratedExpectedMTD: runningRecalExpectedMtd,
        varianceVsCurrentDollar: runningActualMtd - runningCurrentExpectedMtd,
        varianceVsRecalibratedDollar: runningActualMtd - runningRecalExpectedMtd
      };
    });

    const todayIso = new Date().toLocaleDateString("en-CA");
    const completedRows = enrichedRows.filter((row) => String(row?.dateISO) < todayIso);
    const latestCompleted = completedRows.length ? completedRows[completedRows.length - 1] : null;

    return {
      rows: enrichedRows,
      summary: latestCompleted
        ? {
          mtdActual: Number(latestCompleted.actualMTD || 0),
          mtdExpectedCurrent: Number(latestCompleted.currentExpectedMTD || 0),
          mtdExpectedRecalibrated: Number(latestCompleted.recalibratedExpectedMTD || 0),
          varianceVsCurrent: Number(latestCompleted.varianceVsCurrentDollar || 0),
          varianceVsRecalibrated: Number(latestCompleted.varianceVsRecalibratedDollar || 0)
        }
        : null
    };
  }, [goalModelDiagnostics.rows]);

  const movingAverageDays = movingAveragePreset === "custom"
    ? Math.max(1, Math.floor(Number(movingAverageCustomDaysInput) || 5))
    : Math.max(1, Math.floor(Number(movingAveragePreset) || 5));

  const dailySalesGapTrend = useMemo(() => {
    const todayISO = new Date().toLocaleDateString("en-CA");
    const rows = Array.isArray(chartData) ? chartData : [];
    const movingWindowSize = Math.max(1, Math.floor(movingAverageDays));
    const transformed = rows.map((row, index) => {
      const isInScope = String(row?.dateISO || "") <= todayISO;
      const expectedGoal = Number(row?.goal || 0);
      const actualRevenue = Number(row?.actual || 0);
      const webOnly = Number(row?.webOnly || 0);
      const gapPct = isInScope && expectedGoal > 0
        ? ((actualRevenue / expectedGoal) - 1)
        : null;

      if (!Number.isFinite(gapPct)) {
        return { ...row, gapPct: null, gapPctMA: null };
      }

      const windowStart = Math.max(0, index - (movingWindowSize - 1));
      const windowValues = rows
        .slice(windowStart, index + 1)
        .map((point) => {
          const pointInScope = String(point?.dateISO || "") <= todayISO;
          const pointGoal = Number(point?.goal || 0);
          const pointActual = Number(point?.actual || 0);
          if (!pointInScope || pointGoal <= 0) return null;
          const pointGap = (pointActual / pointGoal) - 1;
          return Number.isFinite(pointGap) ? pointGap : null;
        })
        .filter((value) => value !== null);

      const gapPctMA = windowValues.length
        ? (windowValues.reduce((sum, value) => sum + value, 0) / windowValues.length)
        : null;
      const webWindowStart = Math.max(0, index - (movingWindowSize - 1));
      const webWindowValues = rows
        .slice(webWindowStart, index + 1)
        .map((point) => Number(point?.webOnly || 0))
        .filter((value) => Number.isFinite(value));
      const webOnlyMA = webWindowValues.length
        ? (webWindowValues.reduce((sum, value) => sum + value, 0) / webWindowValues.length)
        : null;

      return {
        ...row,
        gapPct,
        gapPctMA: Number.isFinite(gapPctMA) ? gapPctMA : null,
        webOnlyMA: Number.isFinite(webOnlyMA) ? webOnlyMA : webOnly
      };
    });

    const maPoints = transformed
      .map((row) => row.gapPctMA)
      .filter((value) => Number.isFinite(value));
    const latestGapPctMA = maPoints.length ? maPoints[maPoints.length - 1] : null;
    const previousGapPctMA = maPoints.length > 1 ? maPoints[maPoints.length - 2] : null;

    let direction = "Not enough data";
    if (Number.isFinite(latestGapPctMA) && Number.isFinite(previousGapPctMA)) {
      if (latestGapPctMA > previousGapPctMA) {
        direction = "Improving";
      } else if (latestGapPctMA < previousGapPctMA) {
        direction = "Worsening";
      } else {
        direction = "Flat";
      }
    } else if (Number.isFinite(latestGapPctMA)) {
      direction = "Flat";
    }

    return {
      rows: transformed,
      latestGapPctMA,
      previousGapPctMA,
      direction
    };
  }, [chartData, movingAverageDays]);

  useEffect(() => {
    if (environment === "production") {
      return;
    }

    console.log("Smart forecast debug", {
      monthlyGoal,
      salesMTD,
      planMTD,
      completedDaysUsed,
      recentCompletedDaySalesTotal,
      recentCompletedDayRunRate,
      remainingDays,
      forecastRemainingCoreSales,
      remainingExpectedOutlierCount,
      expectedRemainingOutlierUpside,
      intelligentForecast: intelligentForecastValue,
      renderedCardField: "intelligentForecastValue"
    });

    if (!Number.isFinite(intelligentForecastValue)) {
      console.warn("Intelligent Forecast is non-finite.", { intelligentForecastValue });
    }

    if (intelligentForecastValue < salesMTD) {
      console.warn("Intelligent Forecast is below salesMTD.", {
        intelligentForecastValue,
        salesMTD
      });
    }

    if (!monthlyGoal || monthlyGoal <= 0) {
      console.warn("Monthly goal is missing or non-positive.", { monthlyGoal });
    }

    if (!planMTD || planMTD <= 0) {
      console.warn("planMTD is missing or non-positive.", { planMTD });
    }
  }, [
    environment,
    monthlyGoal,
    salesMTD,
    planMTD,
    completedDaysUsed,
    recentCompletedDaySalesTotal,
    recentCompletedDayRunRate,
    remainingDays,
    forecastRemainingCoreSales,
    remainingExpectedOutlierCount,
    expectedRemainingOutlierUpside,
    intelligentForecastValue
  ]);

  const getGoalChartLineProps = (dataKey, baseStroke, options = {}) => {
    const isHovered = hoveredGoalSeries === dataKey;
    const isDimmed = hoveredGoalSeries && !isHovered;
    const defaultDot = options.dot ?? true;
    const defaultStrokeWidth = options.strokeWidth ?? 2;
    const activeDotRadius = options.activeDotRadius ?? 7;
    const dotRadius = options.dotRadius ?? 4;

    return {
      stroke: isHovered ? "#000000" : baseStroke,
      strokeWidth: defaultStrokeWidth,
      strokeOpacity: isHovered ? 1 : (isDimmed ? 0.28 : 1),
      activeDot: defaultDot ? { r: isHovered ? activeDotRadius : 5 } : false,
      dot: defaultDot ? { r: isHovered ? dotRadius : 3 } : false,
      onMouseEnter: () => setHoveredGoalSeries(dataKey),
      onMouseLeave: () => setHoveredGoalSeries(null)
    };
  };

  return (
    <AppProvider>
      <Page
        fullWidth
        title={
          <InlineStack align="start" gap="200">
            <Text variant="headingLg" as="h1">
              {selectedMonthLabel} Sales Goal {formatCurrency(monthlyGoal)}
            </Text>
            {environment !== "production" && (
              <Text tone="critical" variant="bodySm" as="span">DEV</Text>
            )}
          </InlineStack>
        }
        subtitle="The Road to a Million Dollars"
        primaryAction={{ content: refreshLabel, onAction: handleRefresh, loading: isRefreshing }}
        secondaryActions={environment !== "production" ? [{ content: "Perf Dashboard", onAction: () => window.location.assign("/performance") }] : undefined}
      >



        {!storeConnected && (
          <Banner status="critical" title="Store not connected">
            <p>
              Connect your Shopify store to see sales data.{" "}
              <a href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`} style={{ color: "#008060", textDecoration: "underline" }}>
                Connect Store
              </a>
            </p>
          </Banner>
        )}
        <Layout>
          <Layout.Section variant="fullWidth">
            <div className="dashboard-month-switcher">
              <Select
                label="Dashboard month"
                options={DASHBOARD_MONTH_OPTIONS}
                value={selectedMonth}
                onChange={handleSelectedMonthChange}
              />
            </div>
          </Layout.Section>

          {/* 4 Metrics Section */}
          <Layout.Section variant="fullWidth">
            <div className="metrics-four-up">
              <Card sectioned>
                <MetricTile
                  label="Yesterday's Net Sales"
                  value={formatCurrency(salesYesterday)}
                  subValue={`Goal: ${formatCurrency(goalYesterday)}`}

                  sideLabel="Placeholder"
                  sideValue={`${goalYesterday > 0 ? ((salesYesterday / goalYesterday) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(salesMTDWithPOS)}
                  sideTone={
                    goalYesterday > 0 && salesYesterday / goalYesterday >= 1
                      ? "success"
                      : goalYesterday > 0 && salesYesterday / goalYesterday >= 0.8
                        ? "caution"
                        : "critical"
                  }
                />
              </Card>
              <Card sectioned>
                <MetricTile
                  label="Sales Month To Date"
                  value={formatCurrency(salesMTDWithPOS)}
                  subValue={`MTD Target. Should have been: ${formatCurrency(planMTD)} by yesterday.`}

                  sideLabel="MTD Trending"
                  sideValue={`${planMTD > 0 ? ((salesMTDWithPOS / planMTD) * 100).toFixed(1) : 0}%`}
                  sideSubValue={formatCurrency(planMTD > 0 ? monthlyGoal * (salesMTDWithPOS / planMTD) : 0)}
                  sideTone={mtdTone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label={selectedDaySalesLabel}
                  value={formatCurrency(todaysSales)}
                  subValue={`${selectedDayGoalLabel}: ${formatCurrency(todaysGoal)}`}
                  sideLabel={selectedDayPaceLabel}
                  sideValue={`${intradayPacing ? intradayPacePct.toFixed(1) : (todaysGoal > 0 ? ((todaysSales / todaysGoal) * 100).toFixed(1) : 0)}%`}
                  sideSubValue={intradayPacing ? `Expected: ${formatCurrency(intradayExpectedSales)} (${intradayExpectedPct.toFixed(1)}%) by now.` : formatCurrency(salesMTDWithPOS)}
                  sideTone={intradayTone}
                />
              </Card>

              <Card sectioned>
                <MetricTile
                  label="Sales Year To Date"
                  value={`${formatCurrency(yearlyStats?.yearToDate)}`}
                  subValue={`YTD Target: ${formatCurrency(yearlyStats?.ytdPlan)}`}
                  sideLabel="Pace"
                  sideValue={`${(yearlyStats?.ytdPlan ?? 0) > 0 ? (((yearlyStats?.yearToDate ?? 0) / yearlyStats.ytdPlan) * 100).toFixed(1) : 0}%`}
                  sideSubValue={`${formatCurrency(yearlyStats?.projectedYearEnd)} (${formatCurrency(yearlyStats?.goal)})`}
                  sideTone={(yearlyStats?.ytdPlan ?? 0) > 0 && (yearlyStats?.yearToDate ?? 0) >= yearlyStats.ytdPlan ? "success" : "critical"}
                />
              </Card>
            </div>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">WebOps</Text>
                  <Text variant="bodySm" tone="subdued" as="p">Mission-critical website operations</Text>
                </BlockStack>
                <div className="webops-grid">
                  {WEBOPS_ITEMS.map((item) => {
                    const status = webopsStatuses[item.key] || "green";
                    const glossaryDefinition = glossaryTermsMap?.[item.title.toLowerCase()];
                    const tooltipContent = glossaryDefinition
                      ? renderGlossaryTooltipContent(glossaryDefinition)
                      : item.definition;

                    return (
                      <div key={item.key} className="webops-item">
                        <PolarisTooltip content={tooltipContent}>
                          <span className="webops-item-title" style={glossaryTermTextStyle}>
                            {item.title}
                          </span>
                        </PolarisTooltip>
                        <span
                          className={`webops-status-dot webops-status-dot--${status}`}
                          aria-label={`${item.title}: ${status}`}
                          title={status}
                        />
                      </div>
                    );
                  })}
                </div>
              </BlockStack>
            </Card>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <div className="dashboard-grid">

              {/* Today's Scheduled Tech Projects */}
              <div className="dashboard-item span-two-thirds">
                <Card sectioned>
                  <BlockStack gap="100">
                    <Text variant="headingMd" as="h2">Today's Scheduled Tech Projects</Text>
                    <Checkbox
                      label="Show Completed History"
                      checked={showCompleted}
                      onChange={(val) => setShowCompleted(val)}
                    />
                    <DragDropContext
                      onDragEnd={async (result) => {
                        if (!result.destination) return;

                        // Reorder majorProjects directly
                        const updatedProjects = Array.from(majorProjects);
                        const [moved] = updatedProjects.splice(result.source.index, 1);
                        updatedProjects.splice(result.destination.index, 0, moved);

                        // Update sort_order
                        const updatedWithOrder = updatedProjects.map((p, idx) => ({
                          ...p,
                          sort_order: idx
                        }));

                        setMajorProjects(updatedWithOrder);
                        markProjectsDirty();
                      }}
                    >
                      <Droppable droppableId="projects">
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {majorProjects.map((proj, idx) => {
                              const visible = isVisibleProject(proj, showCompleted);
                              return (
                                <Draggable
                                  key={getKey(proj)}
                                  draggableId={`proj-${getKey(proj)}`}
                                  index={idx}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      style={{
                                        display: visible ? "flex" : "none",
                                        marginBottom: 4,
                                        position: "relative",
                                        padding: "6px 8px",
                                        borderRadius: 6,
                                        background: snapshot.isDragging ? "#f4f6f8" : "#fff",
                                        border: "none",
                                        background: "rgb(244 244 244)",
                                        ...provided.draggableProps.style,
                                      }}
                                    >
                                      {/* Drag Handle */}
                                      <div
                                        style={{
                                          position: "absolute",
                                          left: "40px",
                                          top: "13px",
                                          cursor: "grab",
                                        }}
                                      >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                          <circle cx="5" cy="5" r="2" fill="#666" />
                                          <circle cx="5" cy="12" r="2" fill="#666" />
                                          <circle cx="5" cy="19" r="2" fill="#666" />
                                          <circle cx="12" cy="5" r="2" fill="#666" />
                                          <circle cx="12" cy="12" r="2" fill="#666" />
                                          <circle cx="12" cy="19" r="2" fill="#666" />
                                        </svg>
                                      </div>

                                      {/* Completed Checkbox */}
                                      <Checkbox
                                        checked={!!proj.completed_date}
                                        onChange={(checked) => {
                                          const now = checked ? localToMySQL(new Date()) : null;
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, completed_date: now }
                                                : p
                                            )
                                          );
                                          markProjectsDirty();
                                        }}
                                      />



                                      {/* Project Name */}
                                      {proj.completed_date && (
                                        <Tooltip
                                          content={`Completed at: ${new Date(proj.completed_date).toLocaleString("en-US", {
                                            timeZone: "America/Chicago",
                                            month: "numeric",
                                            day: "numeric",
                                            year: "2-digit",
                                            hour: "numeric",
                                            minute: "2-digit",
                                            hour12: true,
                                          })}`}
                                        >
                                          <span style={{ cursor: 'pointer', align: "center", paddingtop: "2px" }}>
                                            {/* Inline calendar SVG */}
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                              <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
                                              <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
                                              <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
                                              <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
                                            </svg>
                                          </span>
                                        </Tooltip>
                                      )}
                                      <input
                                        value={proj.project_name || ""}
                                        onChange={(e) => {
                                          setMajorProjects(prev =>
                                            prev.map(p =>
                                              (p.id ?? p.tempId) === (proj.id ?? proj.tempId)
                                                ? { ...p, project_name: e.target.value }
                                                : p
                                            )
                                          );
                                          markProjectsDirty();
                                        }}
                                        style={{
                                          flex: 1,
                                          padding: "6px 8px 6px 30px",
                                          border: "unset",
                                          textDecoration: isToday(proj.completed_date) ? "line-through" : "none",
                                          opacity: proj.completed_date ? 0.6 : 1
                                        }}
                                      />

                                      {/* Completed Date */}
                                      <ProjectDateTooltip completedDate={proj.completed_date} />

                                      {/* Delete Button */}
                                      <button
                                        onClick={() => {
                                          if (proj.id) {
                                            handleDeleteProject(proj.id);
                                          } else {
                                            setMajorProjects(prev => prev.filter(p => p.tempId !== proj.tempId));
                                            markProjectsDirty();
                                          }
                                        }}
                                        style={{
                                          /* position: "absolute",
                                           zIndex: 10,*/
                                          border: "none",
                                          background: "none",
                                          color: "#666",
                                          right: "3px",
                                          cursor: "pointer",
                                        }}
                                      >
                                        <Icon source={DeleteIcon} />
                                      </button>
                                    </div>
                                  )}
                                </Draggable>
                              )
                            })}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>



                    {/* Add Project Button */}
                    <button
                      onClick={() => {
                        setMajorProjects(prev => [
                          ...prev,
                          {
                            tempId: uuidv4(),
                            project_name: "",
                            completed_date: null
                          }
                        ]);
                        markProjectsDirty();
                      }}
                      style={{
                        marginTop: "0px",
                        padding: "6px 8px",
                        background: "none",
                        border: "1px solid #777",
                        cursor: "pointer"
                      }}
                    >
                      + Add Project
                    </button>





                    <Button
                      variant="primary"
                      disabled={saveStatus === "saving" || !projectsDirty}
                      onClick={saveProjects}
                    >
                      {saveStatus === "idle" && (projectsDirty ? "Save Projects" : "Saved")}
                      {saveStatus === "saving" && `Saving${'.'.repeat(dotCount)}`}
                      {saveStatus === "saved" && "Saved"}

                    </Button>

                    <Button
                      hidden
                      variant="primary"
                      disabled
                      onClick={() => { }}
                    >
                      Legacy Save
                    </Button>
                    {false && (
                      <Button
                        hidden
                        variant="primary"
                        disabled={saveStatus === "saving"}
                        onClick={async () => {
                          setSaveStatus("saving");
                          setSaveColor(undefined);

                          try {
                            // 1️⃣ Prepare payload: only send fields backend expects
                            const payload = majorProjects.map((p, idx) => ({
                              id: p.id || null,
                              tempId: p.tempId || null,   // 👈 ADD THIS
                              project_name: p.project_name || "",
                              completed_date: p.completed_date || null,
                              sort_order: p.sort_order ?? idx
                            }));

                            console.log("Sending projects to backend:", payload);

                            // 2️⃣ Post to backend
                            const saveRes = await api.post("/api/projects", { projects: payload });

                            // 3️⃣ Backend should return full list with real MySQL IDs
                            const refreshed = await api.get("/api/projects");

                            // 4️⃣ Map backend IDs back into frontend state
                            setMajorProjects(
                              refreshed.data.projects
                                .sort((a, b) => a.sort_order - b.sort_order) // 👈 safety net
                                .map(p => ({
                                  id: p.id,
                                  tempId: p.temp_id || uuidv4(), // 👈 keep identity alive
                                  project_name: p.project_name,
                                  completed_date: p.completed_date,
                                  sort_order: p.sort_order
                                }))
                            );

                            setSaveStatus("saved");
                            setSaveColor("#28a745");

                            setTimeout(() => {
                              setSaveStatus("idle");
                              setSaveColor(undefined);
                            }, 2000);

                          } catch (err) {
                            console.error("Failed saving projects:", err);
                            setSaveStatus("idle");
                            console.log("Sending projects to backend:", payload);
                          }
                        }}
                      >
                        {saveStatus === "idle" && "Save Projects"}
                        {saveStatus === "saving" && `Saving${'.'.repeat(dotCount)}`}
                        {saveStatus === "saved" && "Saved"}

                      </Button>
                    )}

                  </BlockStack>
                </Card>
              </div>
              <div className="dashboard-item span-third">
                <BlockStack gap="400">
                  {/* Legacy Sales Yesterday Section */}
                  <div>
                    <Card sectioned>
                      <button
                        onClick={() => setSalesCollapsed(!salesCollapsed)}
                        style={{
                          all: 'unset',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          display: 'block',
                          marginBottom: 4,

                        }}
                      >
                        Sales Yesterday (Net) — {formatCurrency(salesYesterday)}
                      </button>
                      {salesCollapsed && (
                        <BlockStack gap="100" style={{ paddingLeft: 16, flexDirection: 'column' }}>
                          <Metric label="Online Store Yesterday" value={formatCurrency(onlineStoreYesterday)} />
                          <Metric label="POS Yesterday" value={formatCurrency(posYesterday)} />
                          <Metric label="Refunds Yesterday" value={formatCurrency(refundsYesterday)} />
                          <Metric label="POS Month to Date" value={formatCurrency(salesMTDWithPOS - salesMTD)} />
                          <Metric label="Plan to Date" value={formatCurrency(planMTD)} />
                        </BlockStack>
                      )}
                      <Metric label="Monthly Goal" value={formatCurrency(monthlyGoal)} />
                      <Metric
                        label="% to Plan (Web Only)"
                        value={`${percentToPlan}% (${formatCurrency(salesMTD)})`}
                        tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                      />
                      <Metric
                        label="% to Plan (Including POS)"
                        value={`${percentToPlanWithPOS}% (${formatCurrency(salesMTDWithPOS)})`}
                        tone={percentToPlan > 99 ? "success" : percentToPlan < 99 ? "critical" : tone}
                      />

                      {yearlyStats && (
                        <>
                          <Metric label="YTD POS" value={formatCurrency(yearlyStats.posYearToDate)} />
                          <Metric label="YTD Progress" value={`${yearlyStats.progressPct}% (${formatCurrency(yearlyStats.yearToDate)})`} />
                          <Metric label="Projected Year End" value={formatCurrency(yearlyStats.projectedYearEnd)} tone={yearlyStats.projectedYearEnd >= yearlyStats.goal ? "success" : "critical"} />
                        </>
                      )}

                    </Card>
                  </div>

                  {/* Orders Yesterday Section */}
                  <div>
                    <Card sectioned>
                      <BlockStack gap="6"> {/* reasonable gap */}
                        {/* Header with button */}
                        <InlineStack align="space-between">
                          <Text variant="headingMd" as="h2">
                            Orders Yesterday
                          </Text>
                          {orders.length > 3 && (
                            <Button plain className="toggle-orders-btn" onClick={() => setOrdersCollapsed(!ordersCollapsed)}>
                              {ordersCollapsed ? `+${orders.length - 3} more` : '-Collapse'}
                            </Button>
                          )}
                        </InlineStack>

                        {/* Orders list */}
                        {loading ? (
                          <Text>Loading…</Text>
                        ) : orders.length ? (
                          <div className="orders-container">
                            {orders.map((order, idx) => {
                              const isExtra = idx >= 3;
                              return (
                                <div
                                  key={order.id}
                                  className={`order-item ${isExtra ? 'extra' : ''} ${!ordersCollapsed && isExtra ? 'show' : ''
                                    }`}
                                >
                                  <InlineStack align="space-between">
                                    <a
                                      href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/orders/${order.id}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <Text tone="brand">{order.name}</Text>
                                    </a>
                                    <Text>{formatCurrency(order.financials.netYesterday)}</Text>
                                  </InlineStack>

                                  {order.financials.refundsYesterday > 0 && (
                                    <InlineStack align="space-between">
                                      <Text tone="critical">Refunds</Text>
                                      <Text tone="critical">
                                        -{formatCurrency(order.financials.refundsYesterday)}
                                      </Text>
                                    </InlineStack>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <BlockStack gap="2">
                            <Text>No orders available</Text>
                            <a
                              href={`/auth?shop=${process.env.REACT_APP_SHOPIFY_STORE}`}
                              style={{ color: "#008060", textDecoration: "underline" }}
                            >
                              Connect Store
                            </a>
                          </BlockStack>
                        )}
                      </BlockStack>
                    </Card>
                  </div>



                  {/* Top Products MTD Section */}
                  <div>
                    <Card sectioned>
                      <BlockStack gap="200">
                        <Text variant="headingMd" as="h2">
                          Top Products Month-to-Date
                        </Text>

                        {topProductsMTD.length ? (
                          topProductsMTD.map((p, idx) => (
                            <BlockStack key={idx} gap="50">
                              <InlineStack align="center" style={{ width: "100%" }}>
                                {p.productId ? (
                                  <a
                                    href={`https://admin.shopify.com/store/${process.env.REACT_APP_SHOPIFY_STORE}/products/${p.productId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: "none" }}
                                  >
                                    <Text tone="brand">{p.title}</Text>
                                  </a>
                                ) : (
                                  <Text tone="subdued">{p.title}</Text>
                                )}

                                {p.handle ? (
                                  <a
                                    href={`https://${process.env.REACT_APP_SHOPIFY_STORE}.myshopify.com/products/${p.handle}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ display: "flex", alignItems: "center", textDecoration: "none", marginLeft: 12, marginRight: "auto" }}
                                  >
                                    <Text tone="brand">Store</Text>
                                    <Icon source={ExternalIcon} color="base" style={{ marginLeft: 4 }} />
                                  </a>
                                ) : (
                                  <div style={{ marginLeft: 12, marginRight: "auto" }} />
                                )}

                                <Text variant="headingSm">
                                  {formatCurrency(p.revenue)} (qty: {p.quantity})
                                </Text>
                              </InlineStack>
                            </BlockStack>
                          ))
                        ) : (
                          <Text tone="subdued">No product sales this month</Text>
                        )}
                      </BlockStack>
                    </Card>
                  </div>
                </BlockStack>
              </div>
            </div>
          </Layout.Section>

          {/* Percent-of-Goal Chart by Channel */}
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <InlineStack align="space-between" style={{ width: "100%", marginBottom: 16 }}>
                <BlockStack gap="050">
                  <Text variant="headingMd">{monthName} Details</Text>
                </BlockStack>
                <InlineStack gap="300" blockAlign="center">
                  <Button onClick={handleReevaluate} loading={isReevaluating}>
                    Re-evaluate
                  </Button>
                  <Checkbox
                    label="Show Projected Finish Pace"
                    checked={showWebTrendline}
                    onChange={(newValue) => setShowWebTrendline(newValue)}
                  />
                  <Checkbox
                    label={(
                      <span style={{ color: showRealitySeasoning ? "#116dff" : undefined, fontWeight: showRealitySeasoning ? 600 : undefined }}>
                        <GlossaryText text="Intelligent Forecast" terms={glossaryTermsMap} />
                      </span>
                    )}
                    labelClassName="intelligent-forecast-checkbox"
                    checked={showRealitySeasoning}
                    onChange={(newValue) => setShowRealitySeasoning(newValue)}
                  />
                  <Checkbox
                    label={<GlossaryText text="Simulation" terms={glossaryTermsMap} />}
                    checked={showSimulation}
                    onChange={handleSimulationToggle}
                  />
                  {environment !== "production" && (
                    <Checkbox
                      label="Compare Goal Model"
                      checked={compareGoalModel}
                      onChange={(newValue) => setCompareGoalModel(newValue)}
                    />
                  )}
                </InlineStack>
              </InlineStack>
              {salesSummaryBullets.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <ul style={{ margin: 0, paddingLeft: 20 }}>
                    {salesSummaryBullets.map((bullet, index) => (
                      <li key={`${index}-${bullet}`} style={{ marginBottom: 6 }}>
                        <Text tone="subdued" as="span">
                          <GlossaryText text={bullet} terms={glossaryTermsMap} />
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {/* Web Sales Trend Sentence */}
              <Text variant="heading" style={{ marginBottom: 16 }}>
                <GlossaryText text={salesSummary || webSalesTrendSentence(chartData)} terms={glossaryTermsMap} />
              </Text>

              {chartData.length > 0 ? (
                <div className="goal-chart-shell" style={{ position: "relative" }}>
                  <div
                    style={{
                      position: "absolute",
                      top: 8,
                      right: 12,
                      zIndex: 2,
                      background: "rgba(255, 255, 255, 0.94)",
                      border: "1px solid #d9d9d9",
                      borderRadius: 10,
                      padding: "10px 12px",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                      pointerEvents: "none"
                    }}
                  >
                    <BlockStack gap="050">
                      <Text variant="bodySm" tone="subdued" as="p">
                        {monthName} Sales Projected
                      </Text>
                      <Text variant="headingMd" as="p">
                        <span>
                          {formatCurrency(monthSalesProjectedValue)}
                        </span>
                      </Text>
                      <Text variant="bodySm" tone="subdued" as="p">
                        <span title="MTD + recent completed-day pace + expected outlier upside">
                          Intelligent: <span style={{ color: projectedPanelTone.value, fontWeight: 600 }}>{formatCurrency(intelligentForecastValue)}</span>
                        </span>
                      </Text>
                      {showRealitySeasoning && (
                        <Text variant="bodyMd" as="p">
                          <span style={{ color: "#116dff", fontWeight: 600 }}>
                            Trend line only: {chartIntelligentForecastPct.toFixed(1)}%
                          </span>
                        </Text>
                      )}
                    </BlockStack>
                  </div>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart
                      data={goalModelDiagnostics.rows}
                      margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                      onMouseLeave={() => setHoveredGoalSeries(null)}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <ReferenceArea
                        y1={0}
                        y2={100}
                        fill="rgba(200, 0, 0, 0.08)"
                        ifOverflow="extendDomain"
                      />
                      <XAxis dataKey="formattedDate" />
                      <YAxis unit="%" domain={[0, 'auto']} />
                      <Tooltip content={<GoalChartTooltip hoveredSeries={hoveredGoalSeries} compareGoalModel={compareGoalModel} />} offset={44} />
                      <Legend formatter={(value) => value && value.startsWith("Scenario ") ? "" : value} />
                      <Line
                        type="monotone"
                        dataKey="dailyPercentOfGoal"
                        name="% of Goal Web and POS"
                        {...getGoalChartLineProps("dailyPercentOfGoal", "#82ca9d")}
                      />
                      <Line
                        type="monotone"
                        dataKey="dailyWebPercentOfGoal"
                        name="% of Goal Web Only"
                        {...getGoalChartLineProps("dailyWebPercentOfGoal", "#ff5100")}
                      />
                      {showWebTrendline && (
                        <>
                          {webScenarioKeys.map((scenarioKey) => (
                            <Line
                              key={scenarioKey}
                              type="monotone"
                              dataKey={scenarioKey}
                              name={`Trajectory as of ${monthNameAbbr} ${Number(String(scenarioKey).replace("webScenario", ""))}`}
                              legendType="none"
                              stroke="#000000"
                              {...getGoalChartLineProps(
                                scenarioKey,
                                "#000000",
                                {
                                  strokeWidth: 1,
                                  dot: false
                                }
                              )}
                              strokeOpacity={
                                hoveredGoalSeries
                                  ? (hoveredGoalSeries === scenarioKey ? 1 : 0.12)
                                  : webScenarioKeys.length > 1
                                    ? 0.08 + ((webScenarioKeys.indexOf(scenarioKey)) / (webScenarioKeys.length - 1)) * 0.45
                                    : 0.45
                              }
                              isAnimationActive={false}
                              connectNulls={false}
                            />
                          ))}
                          <Line
                            type="linear"
                            dataKey="webTrend"
                            name="Projected Finish Pace"
                            strokeDasharray="6 6"
                            isAnimationActive={true}
                            {...getGoalChartLineProps("webTrend", "#ff5900", { dot: false })}
                          />
                        </>
                      )}
                      {showRealitySeasoning && (
                        <Line
                          type="linear"
                          dataKey="realitySeasoning"
                          name="Intelligent Forecast"
                          stroke="#116dff"
                          strokeDasharray="10 6"
                          isAnimationActive={true}
                          connectNulls={false}
                          {...getGoalChartLineProps("realitySeasoning", "#116dff", { dot: false })}
                        />
                      )}
                      {compareGoalModel && (
                        <>
                          <Line
                            type="monotone"
                            dataKey="recalibratedDailyGoalPct"
                            name="% of Goal Recalibrated"
                            stroke="#2f6fed"
                            strokeDasharray="6 4"
                            {...getGoalChartLineProps("recalibratedDailyGoalPct", "#2f6fed", { dot: false })}
                          />
                          <Line
                            type="monotone"
                            dataKey="varianceVsCurrentGoalPct"
                            name="Variance vs Current Goal %"
                            stroke="#6f42c1"
                            strokeDasharray="2 6"
                            {...getGoalChartLineProps("varianceVsCurrentGoalPct", "#6f42c1", { dot: false })}
                          />
                          <Line
                            type="monotone"
                            dataKey="varianceVsRecalibratedGoalPct"
                            name="Variance vs Recalibrated Goal %"
                            stroke="#009688"
                            strokeDasharray="2 6"
                            {...getGoalChartLineProps("varianceVsRecalibratedGoalPct", "#009688", { dot: false })}
                          />
                        </>
                      )}
                    </LineChart>
                  </ResponsiveContainer>
                  {compareGoalModel && goalModelDiagnostics.stats && (
                    <div style={{ marginTop: 12 }}>
                      <InlineStack gap="400" wrap={true}>
                        <Text variant="bodySm" tone="subdued">Avg abs variance vs old: <strong>{goalModelDiagnostics.stats.avgAbsVarianceOld.toFixed(1)}%</strong></Text>
                        <Text variant="bodySm" tone="subdued">Avg abs variance vs recalibrated: <strong>{goalModelDiagnostics.stats.avgAbsVarianceRecal.toFixed(1)}%</strong></Text>
                        <Text variant="bodySm" tone="subdued">Days over goal (old): <strong>{goalModelDiagnostics.stats.daysOverOld}</strong></Text>
                        <Text variant="bodySm" tone="subdued">Days over goal (recalibrated): <strong>{goalModelDiagnostics.stats.daysOverRecal}</strong></Text>
                        <Text variant="bodySm" tone="subdued">
                          Biggest over day: <strong>{goalModelDiagnostics.stats.biggestOverCurrent ? `${goalModelDiagnostics.stats.biggestOverCurrent.formattedDate} (${Number(goalModelDiagnostics.stats.biggestOverCurrent.varianceVsCurrentGoalPct || 0).toFixed(1)}%)` : "n/a"}</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued">
                          Biggest under day: <strong>{goalModelDiagnostics.stats.biggestUnderCurrent ? `${goalModelDiagnostics.stats.biggestUnderCurrent.formattedDate} (${Number(goalModelDiagnostics.stats.biggestUnderCurrent.varianceVsCurrentGoalPct || 0).toFixed(1)}%)` : "n/a"}</strong>
                        </Text>
                      </InlineStack>
                    </div>
                  )}
                  {pacingDiagnosticsV2 && (
                    <div
                      style={{
                        marginTop: 12,
                        border: "1px solid #d9d9d9",
                        borderRadius: 10,
                        padding: "10px 12px",
                        background: "#f7faf7"
                      }}
                    >
                      <InlineStack gap="400" wrap={true}>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Core Pace: <strong>{Number(pacingDiagnosticsV2.corePacePct || 0).toFixed(1)}%</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Blended Pace: <strong>{Number(pacingDiagnosticsV2.blendedPacePct || 0).toFixed(1)}%</strong>
                        </Text>
                        <Text variant="bodySm" tone="subdued" as="p">
                          Outlier Contribution: <strong>{formatCurrency(Number(pacingDiagnosticsV2.outlierContribution || 0))}</strong>
                        </Text>
                      </InlineStack>
                    </div>
                  )}
                </div>
              ) : (
                <Text>No data for chart</Text>
              )}
              <InlineStack blockAlign="center" style={{ width: "100%", marginTop: 12 }}>
                <div style={{ flex: 1 }}>
                  <Text tone="subdued" variant="bodySm" as="p">
                    Each gray line is a trajectory adjustment based on the its MTD average. These scenario lines show each day's projected month-end pace; the Malta-Orange dotted line is the current projected finish pace.
                  </Text>
                </div>
                <div style={{ marginLeft: "auto" }}>
                  <Button size="slim" onClick={openGlossaryModal}>
                    Glossary
                  </Button>
                </div>
              </InlineStack>
            </Card>
          </Layout.Section>

          {/* Daily Sales vs Goal Chart */}
          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="050">
                <Text variant="headingMd" as="h2">Daily Sales vs Goal</Text>
                <Text variant="bodySm" tone="subdued" as="p">Weighted for day-of-week</Text>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Text variant="bodySm" tone="subdued" as="span">Moving Avg:</Text>
                  <select
                    value={movingAveragePreset}
                    onChange={(event) => setMovingAveragePreset(event.target.value)}
                    style={{ padding: "4px 6px" }}
                    aria-label="Moving average preset"
                  >
                    <option value="3">Fast (3d)</option>
                    <option value="5">Balanced (5d)</option>
                    <option value="7">Weekly (7d)</option>
                    <option value="14">Smooth (14d)</option>
                    <option value="custom">Custom</option>
                  </select>
                  {movingAveragePreset === "custom" && (
                    <input
                      type="number"
                      min={1}
                      max={30}
                      step={1}
                      value={movingAverageCustomDaysInput}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next === "") {
                          setMovingAverageCustomDaysInput("");
                          return;
                        }
                        const parsed = Math.floor(Number(next));
                        if (!Number.isFinite(parsed)) return;
                        const clamped = Math.max(1, Math.min(30, parsed));
                        setMovingAverageCustomDaysInput(String(clamped));
                      }}
                      onBlur={() => {
                        const parsed = Math.floor(Number(movingAverageCustomDaysInput));
                        if (!Number.isFinite(parsed)) {
                          setMovingAverageCustomDaysInput("5");
                          return;
                        }
                        setMovingAverageCustomDaysInput(String(Math.max(1, Math.min(30, parsed))));
                      }}
                      style={{ width: 64, padding: "4px 6px" }}
                      aria-label="Custom moving average days"
                    />
                  )}
                </div>
                <Text variant="bodySm" tone="subdued" as="p">
                  Fast reacts quickly, Balanced is default, Weekly smooths one week, Smooth highlights broader trend.
                </Text>
                {movingAveragePreset === "custom" && (
                  <Text variant="bodySm" tone="subdued" as="p">Custom range: 1-30 days.</Text>
                )}
                <Text variant="bodySm" tone="subdued" as="p">
                  Gap Trend ({movingAverageDays}d): {Number.isFinite(dailySalesGapTrend.latestGapPctMA) ? `${(dailySalesGapTrend.latestGapPctMA * 100).toFixed(1)}%` : "n/a"} • {dailySalesGapTrend.direction}
                </Text>
              </BlockStack>
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={dailySalesGapTrend.rows} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="formattedDate" />
                    <YAxis
                      tickFormatter={(value) =>
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value)
                      }
                    />
                    <Tooltip
                      formatter={(value, name) => [
                        new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(value),
                        name === 'webOnly' ? 'Web Only' : name
                      ]}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="goal" stroke="#8884d8" strokeWidth={2} />
                    <Line type="monotone" dataKey="actual" stroke="#82ca9d" strokeWidth={2} />
                    <Line type="monotone" dataKey="webOnly" stroke="#FFA500" strokeWidth={2} />
                    <Line
                      type="monotone"
                      dataKey="webOnlyMA"
                      name={`Web Only MA (${movingAverageDays}d)`}
                      stroke="#5f6f8f"
                      strokeOpacity={0.6}
                      strokeWidth={1.25}
                      strokeDasharray="6 6"
                      dot={false}
                      connectNulls={true}
                    />
                    {compareGoalModel && (
                      <>
                        <Line type="monotone" dataKey="currentExpectedMTD" name="Expected MTD $ (Current)" stroke="#455a64" strokeDasharray="6 6" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="recalibratedExpectedMTD" name="Recalibrated Expected MTD $" stroke="#1565c0" strokeDasharray="8 5" strokeWidth={2} dot={false} />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <Text>No data for chart</Text>
              )}
              {compareGoalModel && revenueGoalDiagnostics.summary && (
                <div style={{ marginTop: 12 }}>
                  <InlineStack gap="400" wrap={true}>
                    <Text variant="bodySm" tone="subdued">MTD actual: <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdActual)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">MTD expected (current): <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdExpectedCurrent)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">MTD expected (recalibrated): <strong>{formatCurrency(revenueGoalDiagnostics.summary.mtdExpectedRecalibrated)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">Variance vs current: <strong>{formatCurrency(revenueGoalDiagnostics.summary.varianceVsCurrent)}</strong></Text>
                    <Text variant="bodySm" tone="subdued">Variance vs recalibrated: <strong>{formatCurrency(revenueGoalDiagnostics.summary.varianceVsRecalibrated)}</strong></Text>
                  </InlineStack>
                </div>
              )}
            </Card>
          </Layout.Section>

          <Layout.Section variant="fullWidth">
            <Card sectioned>
              <BlockStack gap="400">
                <BlockStack gap="100">
                  <Text variant="headingMd" as="h2">
                    <GlossaryText text="Traffic Signals (Human Only)" terms={glossaryTermsMap} />
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    {trafficSummaryText}
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    <GlossaryText
                      text="Active-month comparisons use completed days only, so signals reflect prior-month completed-day MTD rather than a full prior month."
                      terms={glossaryTermsMap}
                    />
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    Solid lines show historical actuals. Dotted lines show forecast direction. The vertical April marker compares current actuals with the same period last month.
                  </Text>
                </BlockStack>

                {trafficError ? (
                  <Banner status="critical">{trafficError}</Banner>
                ) : trafficLoading ? (
                  <Text>Loading traffic analytics...</Text>
                ) : (
                  <>
                    <div className="traffic-summary-grid">
                      {Object.entries(TRAFFIC_SERIES_CONFIG).map(([trafficKey, config]) => (
                        <TrafficSignalCard
                          key={trafficKey}
                          label={config.label.replace(" Sessions", "")}
                          summary={trafficSummary?.[trafficKey]}
                          signal={trafficSignals?.[trafficKey]}
                          comparisonWindow={trafficComparisonWindow}
                          terms={glossaryTermsMap}
                        />
                      ))}
                    </div>

                    <div className="traffic-chart-grid">
                      {
                        Object.entries(TRAFFIC_SERIES_CONFIG).map(([trafficKey, config]) => (
                          <TrafficTrendCard
                            key={trafficKey}
                            label={config.label}
                            color={config.color}
                            data={trafficCharts?.[trafficKey] || []}
                            summary={trafficSummary?.[trafficKey]}
                            terms={glossaryTermsMap}
                          />
                        ))}
                    </div>

                    <div className="traffic-top-grid">
                      {Object.entries(TRAFFIC_SERIES_CONFIG).map(([trafficKey, config]) => (
                        <TrafficTopChannelsCard
                          key={trafficKey}
                          label={config.label.replace(" Sessions", "")}
                          channels={topReferringChannels?.[trafficKey] || []}
                          terms={glossaryTermsMap}
                        />
                      ))}
                    </div>

                    <InlineStack blockAlign="center" style={{ width: "100%", marginTop: 12 }}>
                      <div style={{ marginLeft: "auto" }}>
                        <Button size="slim" onClick={openGlossaryModal}>
                          Glossary
                        </Button>
                      </div>
                    </InlineStack>
                  </>
                )}
              </BlockStack>
            </Card>
          </Layout.Section>





          <Layout.Section variant="fullWidth">
            <Text variant="bodySm" tone="subdued">Environment: {environment.toUpperCase()}</Text>
          </Layout.Section>
        </Layout>
      </Page>
      <Modal
        open={simulationModalOpen}
        onClose={closeSimulationModal}
        title="Simulation"
        size="large"
        primaryAction={{
          content: "Done",
          onAction: closeSimulationModal
        }}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {simulationDraft.map((entry, index) => (
              <div key={entry.id} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <InlineStack align="space-between" blockAlign="center" style={{ width: "100%" }}>
                  <div style={{ flex: 1, marginLeft: 20 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <Text variant="bodySm" tone="subdued" as="span">Add a Day's Sales:</Text>
                      <Text variant="headingMd" as="span">$</Text>
                      <input
                        value={formatSimulationAmount(entry.amount)}
                        onChange={(event) => handleSimulationDraftChange(index, event.target.value)}
                        placeholder="0"
                        inputMode="decimal"
                        style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6 }}
                      />
                    </div>
                  </div>
                  <Button destructive onClick={() => removeSimulationDraftRow(index)}>
                    Delete
                  </Button>
                </InlineStack>
              </div>
            ))}
            <div>
              <Button onClick={addSimulationDraftRow}>Add Another</Button>
            </div>
          </BlockStack>
        </Modal.Section>
      </Modal>
      <Modal
        open={glossaryModalOpen}
        onClose={() => setGlossaryModalOpen(false)}
        title="Glossary"
        size="large"
        primaryAction={{
          content: glossarySaving ? "Saving..." : "Save",
          onAction: saveGlossary,
          loading: glossarySaving
        }}
        secondaryActions={[
          {
            content: "Add Term",
            onAction: addGlossaryDraftRow
          }
        ]}
      >
        <Modal.Section>
          <BlockStack gap="300">
            {glossaryDraft.map((entry, index) => (
              <div key={entry.id} style={{ border: "1px solid #d9d9d9", borderRadius: 8, padding: 12 }}>
                <BlockStack gap="200">
                  <input
                    value={entry.term}
                    onChange={(event) => handleGlossaryDraftChange(index, "term", event.target.value)}
                    placeholder="Term"
                    style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6 }}
                  />
                  <textarea
                    value={entry.definition}
                    onChange={(event) => handleGlossaryDraftChange(index, "definition", event.target.value)}
                    placeholder="Definition"
                    rows={4}
                    style={{ width: "100%", padding: 8, border: "1px solid #c9cccf", borderRadius: 6, resize: "vertical" }}
                  />
                  <InlineStack align="end">
                    <Button destructive onClick={() => removeGlossaryDraftRow(index)}>
                      Delete
                    </Button>
                  </InlineStack>
                </BlockStack>
              </div>
            ))}
          </BlockStack>
        </Modal.Section>
      </Modal>
    </AppProvider>
  );
}

const isToday = (dateStr) => {
  if (!dateStr) return false;

  const today = new Date();
  const d = new Date(dateStr);

  return (
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate()
  );
};

const isVisibleProject = (p, showCompleted) => {
  return !p.completed_date || isToday(p.completed_date) || showCompleted;
};

function Metric({ label, value, tone }) {
  return (
    <InlineStack align="space-between">
      <Text>{label}</Text>
      <Text variant="headingLg" tone={tone}>{value}</Text>
    </InlineStack>
  );
}

function MetricTile({
  label,
  value,
  subValue,
  tone,
  sideLabel,
  sideValue,
  sideSubValue,
  sideTone
}) {
  return (
    <InlineStack align="space-between" blockAlign="start" style={{ width: "100%" }}>
      <BlockStack gap="150">
        <Text variant="bodySm" tone="subdued">{label}</Text>
        <Text variant="headingLg" tone={tone}>{value}</Text>
        {subValue ? (
          <Text variant="bodySm" tone="subdued">{subValue}</Text>
        ) : null}
      </BlockStack>
      {sideLabel || sideValue || sideSubValue ? (
        <div className="metric-tile-side">
          <BlockStack gap="150">
            <Text variant="bodySm" tone="subdued">{sideLabel}</Text>
            <Text variant="headingLg" tone={sideTone}>{sideValue}</Text>
            <Text variant="bodySm" tone="subdued">{sideSubValue}</Text>
          </BlockStack>
        </div>
      ) : null}
    </InlineStack>
  );
}

function localToMySQL(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

function webSalesTrendSentence(chartData) {
  if (!chartData || chartData.length === 0) return "No web sales data available.";

  const validData = chartData.filter(d => d.mtdWebPercentOfGoal !== undefined);
  if (validData.length < 2) return "Not enough data to calculate trend.";

  // Calculate "below/above goal" percentages
  const startPctBelow = 100 - validData[0].mtdWebPercentOfGoal;
  const endPctBelow = 100 - validData[validData.length - 1].mtdWebPercentOfGoal;

  const startWord = startPctBelow >= 0 ? "below" : "above";
  const endWord = endPctBelow >= 0 ? "below" : "above";

  // Rate of change
  const changePct = endPctBelow - startPctBelow;
  const daysElapsed = validData.length - 1;
  const dailyRate = changePct / daysElapsed;
  const monthlyRate = dailyRate * 30;

  const droppingIncreasing = dailyRate >= 0 ? "dropping" : "increasing";

  return `Web sales started the month at ${Math.abs(startPctBelow).toFixed(0)}% ${startWord} goal and are now ${Math.abs(endPctBelow).toFixed(0)}% ${endWord} goal, ${droppingIncreasing} at a rate of ${Math.abs(dailyRate).toFixed(2)}% per day (${Math.abs(monthlyRate).toFixed(1)}%/month).`;
}

function ProjectDateTooltip({ completedDate }) {
  if (!completedDate) return null;

  const displayText = `Completed at: ${new Date(completedDate).toLocaleString("en-US", {
    timeZone: "America/Chicago",
    month: "numeric",
    day: "numeric",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })}`;

  return (
    <PolarisTooltip content={displayText}>
      <span style={{ cursor: "pointer", position: "relative", top: "4px", left: "4px" }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <rect x="3" y="4" width="18" height="18" stroke="#666" strokeWidth="2" />
          <line x1="3" y1="10" x2="21" y2="10" stroke="#666" strokeWidth="2" />
          <line x1="8" y1="4" x2="8" y2="0" stroke="#666" strokeWidth="2" />
          <line x1="16" y1="4" x2="16" y2="0" stroke="#666" strokeWidth="2" />
        </svg>
      </span>
    </PolarisTooltip>
  );
}

function buildGlossarySplitRegex(terms) {
  const glossaryKeys = Object.keys(terms || {});
  if (!glossaryKeys.length) {
    return null;
  }

  return new RegExp(
    `(${glossaryKeys
      .sort((a, b) => b.length - a.length)
      .map((term) => `\\b${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`)
      .join("|")})`,
    "gi"
  );
}

function renderGlossaryTooltipContent(text) {
  const rawText = String(text || "").trim();
  if (!rawText) {
    return "";
  }

  const renderInlineGlossaryFormatting = (value) => {
    const segments = String(value || "")
      .split(/(<(?:b|strong)>[\s\S]*?<\/(?:b|strong)>)/gi)
      .filter(Boolean);

    return segments.map((segment, index) => {
      const boldMatch = segment.match(/^<(?:b|strong)>([\s\S]*?)<\/(?:b|strong)>$/i);
      if (boldMatch) {
        return <strong key={`${index}-${boldMatch[1].slice(0, 12)}`}>{boldMatch[1]}</strong>;
      }

      return <React.Fragment key={`${index}-${segment.slice(0, 12)}`}>{segment}</React.Fragment>;
    });
  };

  const paragraphMatches = [...rawText.matchAll(/<p>([\s\S]*?)<\/p>/gi)];
  const paragraphs = paragraphMatches.length
    ? paragraphMatches.map((match) => String(match[1] || "").trim()).filter(Boolean)
    : rawText
      .split(/\n\s*\n/)
      .map((part) => part.trim())
      .filter(Boolean);

  if (!paragraphs.length) {
    return rawText.replace(/<\/?p>/gi, "").trim();
  }

  return (
    <div style={{ maxWidth: "35rem", lineHeight: 1.45 }}>
      {paragraphs.map((paragraph, index) => (
        <p key={`${index}-${paragraph.slice(0, 12)}`} style={{ margin: index === paragraphs.length - 1 ? 0 : "0 0 0.55rem 0" }}>
          {renderInlineGlossaryFormatting(paragraph.replace(/<\/?p>/gi, ""))}
        </p>
      ))}
    </div>
  );
}

const glossaryTermTextStyle = {
  textDecoration: "underline dotted",
  textUnderlineOffset: "2px",
  cursor: "help"
};

function renderPlainTextWithGlossary(text, terms = {}, keyPrefix = "plain") {
  const splitRegex = buildGlossarySplitRegex(terms);
  if (!splitRegex) {
    return String(text || "");
  }

  const parts = String(text || "").split(splitRegex);

  return parts.map((part, index) => {
    const glossaryText = terms[part.toLowerCase()];

    if (!glossaryText) {
      return <React.Fragment key={`${keyPrefix}-${part}-${index}`}>{part}</React.Fragment>;
    }

    return (
      <PolarisTooltip key={`${keyPrefix}-${part}-${index}`} content={renderGlossaryTooltipContent(glossaryText)}>
        <span style={glossaryTermTextStyle}>
          {part}
        </span>
      </PolarisTooltip>
    );
  });
}

function renderSafeHtmlWithGlossary(text, terms = {}, keyPrefix = "html") {
  const rawText = String(text || "");
  if (!rawText.trim()) {
    return "";
  }

  if (!/[<>]/.test(rawText) || typeof DOMParser === "undefined") {
    return renderPlainTextWithGlossary(rawText, terms, keyPrefix);
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${rawText}</div>`, "text/html");
  const root = doc.body.firstChild;

  const renderNode = (node, path) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return renderPlainTextWithGlossary(node.textContent || "", terms, `${keyPrefix}-${path}`);
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const tagName = node.tagName.toLowerCase();
    const childNodes = Array.from(node.childNodes)
      .map((child, index) => renderNode(child, `${path}-${index}`))
      .flat()
      .filter((child) => child !== null && child !== undefined);

    if (tagName === "b" || tagName === "strong") {
      return <strong key={`${keyPrefix}-${path}`}>{childNodes}</strong>;
    }

    if (tagName === "i" || tagName === "em") {
      return <em key={`${keyPrefix}-${path}`}>{childNodes}</em>;
    }

    if (tagName === "u") {
      return <u key={`${keyPrefix}-${path}`}>{childNodes}</u>;
    }

    if (tagName === "br") {
      return <br key={`${keyPrefix}-${path}`} />;
    }

    if (tagName === "p") {
      return (
        <React.Fragment key={`${keyPrefix}-${path}`}>
          {childNodes}
          <br />
          <br />
        </React.Fragment>
      );
    }

    if (tagName === "span" || tagName === "div") {
      return <React.Fragment key={`${keyPrefix}-${path}`}>{childNodes}</React.Fragment>;
    }

    return <React.Fragment key={`${keyPrefix}-${path}`}>{childNodes}</React.Fragment>;
  };

  const renderedNodes = Array.from(root?.childNodes || [])
    .map((node, index) => renderNode(node, `${index}`))
    .flat()
    .filter((node) => node !== null && node !== undefined);

  if (!renderedNodes.length) {
    return renderPlainTextWithGlossary(rawText, terms, keyPrefix);
  }

  if (renderedNodes.length >= 2) {
    const trailingBreaks = renderedNodes.slice(-2);
    if (
      React.isValidElement(trailingBreaks[0]) && trailingBreaks[0].type === "br" &&
      React.isValidElement(trailingBreaks[1]) && trailingBreaks[1].type === "br"
    ) {
      renderedNodes.splice(-2, 2);
    }
  }

  return renderedNodes;
}

function GlossaryText({ text, terms = {} }) {
  return renderSafeHtmlWithGlossary(text, terms);
}

function GoalChartTooltip({ active, label, payload, hoveredSeries = null, compareGoalModel = false }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }
  const point = payload[0]?.payload || {};
  const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

  const formatSeriesName = (entry) => {
    const rawName = entry?.name;
    const rawKey = entry?.dataKey;

    if (typeof rawName === "string" && rawName.trim()) {
      return rawName;
    }

    const scenarioMatch = String(rawKey || "").match(/^webScenario(\d+)$/);
    if (scenarioMatch) {
      return `Scenario Day ${scenarioMatch[1]}`;
    }

    return rawKey;
  };

  const sortedPayload = [...payload]
    .filter((entry) => entry?.value !== null && entry?.value !== undefined)
    .sort((a, b) => {
      if (b.value !== a.value) {
        return b.value - a.value;
      }

      if (a.dataKey === "webTrend") return -1;
      if (b.dataKey === "webTrend") return 1;

      return 0;
    });

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        padding: "14px 16px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <div style={{ marginBottom: 6 }}>{label}</div>
      {sortedPayload.map((entry) => (
        <div
          key={entry.dataKey}
          style={{
            color: hoveredSeries === entry.dataKey ? "#000000" : entry.color,
            marginBottom: 2,
            fontWeight: hoveredSeries === entry.dataKey ? 700 : 400,
            background: hoveredSeries === entry.dataKey ? "rgba(0, 0, 0, 0.06)" : "transparent",
            borderRadius: 6,
            padding: hoveredSeries === entry.dataKey ? "4px 6px" : "0"
          }}
        >
          {formatSeriesName(entry)}: {Number(entry.value).toFixed(1)}%
        </div>
      ))}
      {compareGoalModel && (
        <div style={{ marginTop: 8, borderTop: "1px solid #ececec", paddingTop: 8 }}>
          <div>Actual Sales: {formatCurrency(point.webOnly)}</div>
          <div>Old Goal: {formatCurrency(point.oldDailyGoal)}</div>
          <div>Old Variance: {Number(point.varianceVsCurrentGoalPct || 0).toFixed(1)}%</div>
          <div>Recal Goal: {formatCurrency(point.recalibratedDailyGoal)}</div>
          <div>Recal Variance: {Number(point.varianceVsRecalibratedGoalPct || 0).toFixed(1)}%</div>
        </div>
      )}
    </div>
  );
}

function RevenueGoalTooltip({ active, label, payload, compareGoalModel = false }) {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const point = payload[0]?.payload || {};
  const formatCurrency = (value) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(Number(value || 0));

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #d9d9d9",
        padding: "12px 14px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.12)"
      }}
    >
      <div style={{ marginBottom: 6, fontWeight: 600 }}>{label}</div>
      <div>Actual Revenue: {formatCurrency(point.actualRevenue)}</div>
      <div>Current Expected $: {formatCurrency(point.currentExpectedRevenue)}</div>
      {compareGoalModel && (
        <>
          <div>Recalibrated Expected $: {formatCurrency(point.recalibratedExpectedRevenue)}</div>
          <div>Variance vs Current (MTD): {formatCurrency(point.varianceVsCurrentDollar)}</div>
          <div>Variance vs Recalibrated (MTD): {formatCurrency(point.varianceVsRecalibratedDollar)}</div>
        </>
      )}
    </div>
  );
}

function formatTrafficMonthLabel(monthLabel) {
  if (!monthLabel) {
    return "";
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return monthLabel;
  }

  return parsed.toLocaleString("en-US", { month: "short" });
}

function formatTrafficCompletedRangeLabel(monthLabel, completedDay) {
  const monthText = formatTrafficMonthLabel(monthLabel);
  if (!monthText || !completedDay) {
    return monthText;
  }

  return `${monthText} 1-${completedDay}`;
}

function parseMonthLabel(monthLabel) {
  if (!monthLabel) {
    return null;
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function isActiveTrafficMonth(monthLabel) {
  if (!monthLabel) {
    return false;
  }

  const parsed = new Date(`${monthLabel}-01T00:00:00`);
  const now = new Date();

  return !Number.isNaN(parsed.getTime())
    && parsed.getFullYear() === now.getFullYear()
    && parsed.getMonth() === now.getMonth();
}

function buildTrafficChartSeries(data = []) {
  if (!Array.isArray(data) || !data.length) {
    return [];
  }

  const rows = data.map((point) => ({
    ...point,
    axisLabel: formatTrafficMonthLabel(point.month),
    historicalActual: Number(point.sessions || 0),
    forecastTrend: null,
    forecastPoint: null,
    currentMonthActual: null,
    projectedMonthEnd: null,
    isActiveMonth: false
  }));

  const lastRow = rows[rows.length - 1];
  if (!lastRow || !isActiveTrafficMonth(lastRow.month)) {
    return rows;
  }

  const now = new Date();
  const observedDays = Math.max(Math.min(now.getDate(), new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()), 1);
  const completedDays = Math.max(now.getDate() - 1, 1);
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();

  if (completedDays >= daysInMonth || rows.length < 2) {
    return rows;
  }

  const completedDayActual = Math.round(Number(lastRow.historicalActual || 0) * (completedDays / observedDays));
  const projectedMonthEnd = Math.round((completedDayActual / completedDays) * daysInMonth);
  const previousRow = rows[rows.length - 2];

  previousRow.forecastTrend = Number(previousRow.historicalActual || 0);
  lastRow.historicalActual = null;
  lastRow.axisLabel = formatTrafficCompletedRangeLabel(lastRow.month, completedDays);
  lastRow.forecastTrend = null;
  lastRow.forecastPoint = null;
  lastRow.currentMonthActual = completedDayActual;
  lastRow.projectedMonthEnd = projectedMonthEnd;
  lastRow.isActiveMonth = true;

  rows.push({
    month: `${lastRow.month}-eom`,
    axisLabel: `${formatTrafficMonthLabel(lastRow.month)} EOM`,
    historicalActual: null,
    forecastTrend: projectedMonthEnd,
    forecastPoint: projectedMonthEnd,
    currentMonthActual: null,
    projectedMonthEnd,
    isActiveMonth: false,
    isProjectedEom: true
  });

  return rows;
}

function formatTrafficPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "n/a";
  }

  const sign = numeric > 0 ? "+" : "";
  return `${sign}${numeric.toFixed(1)}%`;
}

function getTrafficSignalTone(status) {
  if (status === "up") return "success";
  if (status === "watch") return "caution";
  if (status === "alert" || status === "down") return "critical";
  return "subdued";
}

function getTrafficSignalStatusFromPct(pctChange) {
  const numeric = Number(pctChange);

  if (!Number.isFinite(numeric)) return "~flat";
  if (numeric < -25) return "alert";
  if (numeric < -10) return "down";
  if (numeric > 10) return "up";
  return "~flat";
}

function buildTrafficComparisonRangeLabel(comparisonWindow = null) {
  const currentMonth = parseMonthLabel(comparisonWindow?.currentMonth);
  const priorMonth = parseMonthLabel(comparisonWindow?.priorMonth);
  const currentComparableDays = Number(comparisonWindow?.currentComparableDays || 0);
  const priorComparableDays = Number(comparisonWindow?.priorComparableDays || 0);

  if (currentMonth && priorMonth && currentComparableDays > 0 && priorComparableDays > 0) {
    const currentMonthLabel = currentMonth.toLocaleString("en-US", { month: "short" });
    const priorMonthLabel = priorMonth.toLocaleString("en-US", { month: "short" });
    return `${currentMonthLabel} 1-${currentComparableDays} vs ${priorMonthLabel} 1-${priorComparableDays}`;
  }

  const now = new Date();
  const fallbackCurrentMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const fallbackPriorMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const completedDay = Math.max(now.getDate() - 1, 1);
  const fallbackCurrentEndDay = Math.min(completedDay, new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate());
  const fallbackPriorEndDay = Math.min(completedDay, new Date(now.getFullYear(), now.getMonth(), 0).getDate());
  const fallbackCurrentMonthLabel = fallbackCurrentMonth.toLocaleString("en-US", { month: "short" });
  const fallbackPriorMonthLabel = fallbackPriorMonth.toLocaleString("en-US", { month: "short" });
  const currentMonthLabel = fallbackCurrentMonthLabel;
  const priorMonthLabel = fallbackPriorMonthLabel;
  const currentMonthEndDay = fallbackCurrentEndDay;
  const priorMonthEndDay = fallbackPriorEndDay;

  return `${currentMonthLabel} 1–${currentMonthEndDay} vs ${priorMonthLabel} 1–${priorMonthEndDay}`;
}

function getTrafficSignalDisplayLabel(status) {
  if (status === "insufficient_data") return "too early";
  if (status === "watch") return "! Needs Attention";
  return status || "~flat";
}

function buildTrafficSummarySentence(signals, summary = {}) {
  const entries = Object.entries(TRAFFIC_SERIES_CONFIG)
    .map(([key, config]) => ({
      key,
      label: config.label.replace(" Sessions", ""),
      status: signals?.[key]?.status || "~flat",
      pctChange: Number(signals?.[key]?.pctChange),
      currentMTD: Number(summary?.[key]?.currentMTD || 0),
      priorMTD: Number(summary?.[key]?.priorMTD || 0)
    }))
    .filter((entry) => Number.isFinite(entry.pctChange));

  if (!entries.length) {
    return "Traffic signals will appear here once the monthly traffic feed is available.";
  }

  const insufficientCount = entries.filter((entry) => entry.status === "insufficient_data").length;
  if (insufficientCount === entries.length) {
    return "Traffic comparisons are still too early to judge reliably this month.";
  }

  const strongest = [...entries].sort((a, b) => b.pctChange - a.pctChange)[0];
  const weakest = [...entries].sort((a, b) => a.pctChange - b.pctChange)[0];
  const strongestLabel = getTrafficSignalDisplayLabel(strongest.status);
  const weakestLabel = getTrafficSignalDisplayLabel(weakest.status);

  if (strongest.key === weakest.key) {
    return `${strongest.label} is ${strongestLabel} at ${formatTrafficPercent(strongest.pctChange)} versus prior-month completed-day MTD traffic.`;
  }

  return `${strongest.label} is ${strongestLabel} at ${formatTrafficPercent(strongest.pctChange)}, while ${weakest.label} is ${weakestLabel} at ${formatTrafficPercent(weakest.pctChange)} versus prior-month completed-day MTD traffic.`;
}

function TrafficSignalCard({ label, summary, signal, comparisonWindow = null, terms = {} }) {
  const status = signal?.status || "~flat";
  const statusLabel = getTrafficSignalDisplayLabel(status);
  const rangeLabel = buildTrafficComparisonRangeLabel(comparisonWindow);
  const comparisonLabel = status === "insufficient_data"
    ? "Too early for a reliable completed-day comparison"
    : `${Number(summary?.currentMTD || 0).toLocaleString("en-US")} vs ${Number(summary?.priorMTD || 0).toLocaleString("en-US")} (${rangeLabel})`;

  return (
    <div className="traffic-summary-card">
      <InlineStack align="space-between" blockAlign="center">
        <Text variant="headingSm" as="h3">
          <GlossaryText text={label} terms={terms} />
        </Text>
        <span className={`traffic-status-pill traffic-status-pill--${status}`}>
          {statusLabel}
        </span>
      </InlineStack>
      <BlockStack gap="050">
        <Text variant="headingLg" tone={getTrafficSignalTone(status)}>
          {formatTrafficPercent(signal?.pctChange)}
        </Text>
        <Text variant="bodySm" tone="subdued">
          {comparisonLabel}
        </Text>
      </BlockStack>
    </div>
  );
}

function TrafficTrendCard({ label, color, data, summary, terms = {} }) {
  const TargetCrosshair = ({ cx, cy, stroke }) => {
    const size = 10; // length of each arm (px)

    return (
      <g>
        {/* vertical */}
        <line
          x1={cx}
          y1={cy - size}
          x2={cx}
          y2={cy + size}
          stroke={stroke}
          strokeWidth={1}
          strokeOpacity={0.8}
        />
        {/* horizontal */}
        <line
          x1={cx - size}
          y1={cy}
          x2={cx + size}
          y2={cy}
          stroke={stroke}
          strokeWidth={1}
          strokeOpacity={0.8}
        />
      </g>
    );
  };
  const baseRows = buildTrafficChartSeries(data);
  const activeMonthRow = baseRows.find((row) => row.isActiveMonth);
  const projectedEomRow = baseRows.find((row) => row.isProjectedEom);
  const priorSamePeriod = Number(summary?.priorMTD || 0);
  const comparisonGapColor = activeMonthRow
    ? (Number(activeMonthRow.currentMonthActual || 0) >= priorSamePeriod ? "#1b8a3a" : "#b71c1c")
    : color;

  if (activeMonthRow) {
    activeMonthRow.priorSamePeriod = priorSamePeriod;
  }

  const chartRows = baseRows.map((row) => {
    if (!row.isActiveMonth) {
      return {
        ...row,
        actualPillar: null,
        gapBandBase: null,
        gapBandValue: null
      };
    }

    const actual = Number(row.currentMonthActual || 0);
    const expected = Number(priorSamePeriod || 0);

    return {
      ...row,
      actualPillar: actual,
      gapBandBase: Math.min(actual, expected),
      gapBandValue: Math.abs(expected - actual)
    };
  });



  return (
    <div className="traffic-chart-card">
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">
          <GlossaryText text={label} terms={terms} />
        </Text>
        {chartRows.length > 0 ? (
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart
              data={chartRows}
              margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
              barCategoryGap="0%"
              barGap={0}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="axisLabel" />
              <YAxis tickFormatter={(value) => Number(value || 0).toLocaleString("en-US")} />
              <Tooltip content={<TrafficChartTooltip label={label} />} />

              <Bar
                dataKey="actualPillar"
                fill={color}
                fillOpacity={0.9}
                isAnimationActive={false}
                legendType="none"
                barSize={12}
                radius={[8, 8, 0, 0]}
              />

              <Line
                type="monotone"
                dataKey="historicalActual"
                stroke={color}
                strokeWidth={2}
                dot={{ r: 3 }}
                activeDot={{ r: 5 }}
              />
              <Line
                type="monotone"
                dataKey="forecastTrend"
                stroke={color}
                strokeWidth={2}
                strokeDasharray="3 5"
                dot={false}
                activeDot={false}
                connectNulls={true}
              />

              {activeMonthRow ? (
                <>
                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={0} // hide default dot
                    shape={(props) => (
                      <TargetCrosshair
                        cx={props.cx}
                        cy={props.cy}
                        stroke={comparisonGapColor}
                      />
                    )}
                  />

                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={6}
                    fill="#ffffff3a"
                    stroke={comparisonGapColor}
                    strokeWidth={2}
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={activeMonthRow.axisLabel}
                    y={priorSamePeriod}
                    r={2.5}
                    fill={comparisonGapColor}
                    stroke="none"
                    ifOverflow="extendDomain"
                  />
                  <ReferenceDot
                    x={(projectedEomRow || activeMonthRow).axisLabel}
                    y={activeMonthRow.projectedMonthEnd}
                    r={3}
                    fill="#ffffff3a"
                    stroke={color}
                    strokeWidth={2}
                  />

                </>
              ) : null}
            </ComposedChart>
          </ResponsiveContainer>
        ) : (
          <Text tone="subdued">No traffic data available.</Text>
        )}
      </BlockStack>
    </div>
  );
}

function TrafficChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) {
    return null;
  }

  const point = payload[0]?.payload || {};
  const historicalActualEntry = payload.find((entry) => entry.dataKey === "historicalActual");
  const forecastEntry = payload.find((entry) => entry.dataKey === "forecastTrend");
  const isActiveMonth = Boolean(point?.isActiveMonth);
  const isProjectedEom = Boolean(point?.isProjectedEom);

  return (
    <div className="traffic-tooltip">
      <BlockStack gap="050">
        <Text variant="bodySm" tone="subdued">{label}</Text>
        <Text variant="headingSm">{point.axisLabel || formatTrafficMonthLabel(point.month)}</Text>
        {isProjectedEom ? (
          <>
            <Text variant="bodySm">Projected EOM: {Number(point.projectedMonthEnd || forecastEntry?.value || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm" tone="subdued">Dotted line shows forecast direction into month-end.</Text>
          </>
        ) : isActiveMonth ? (
          <>
            <Text variant="bodySm">{point.axisLabel}: {Number(point.currentMonthActual || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm">Same period last month: {Number(point.priorSamePeriod || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm">{formatTrafficMonthLabel(point.month)} Projected EOM: {Number(point.projectedMonthEnd || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm" tone="subdued">Vertical marker = current actual vs same period last month.</Text>
          </>
        ) : (
          <>
            <Text variant="bodySm">Sessions: {Number(historicalActualEntry?.value || point.historicalActual || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm">Orders: {Number(point.orders || 0).toLocaleString("en-US")}</Text>
            <Text variant="bodySm">
              Sales: {new Intl.NumberFormat("en-US", {
                style: "currency",
                currency: "USD",
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(Number(point.total_sales || 0))}
            </Text>
            <Text variant="bodySm">Conversion: {formatTrafficPercent(Number(point.conversion_rate || 0) * 100)}</Text>
            {forecastEntry?.value !== null && forecastEntry?.value !== undefined ? (
              <Text variant="bodySm" tone="subdued">Dotted line shows forecast direction into month-end.</Text>
            ) : null}
          </>
        )}
      </BlockStack>
    </div>
  );
}

function TrafficTopChannelsCard({ label, channels, terms = {} }) {
  return (
    <div className="traffic-top-card">
      <BlockStack gap="200">
        <Text variant="headingSm" as="h3">
          <GlossaryText text={`${label} Top Channels`} terms={terms} />
        </Text>
        {Array.isArray(channels) && channels.length > 0 ? (
          <div className="traffic-channel-list">
            {channels.map((channel) => (
              <div key={channel.referring_channel} className="traffic-channel-row">
                <div className="traffic-channel-main">
                  <Text variant="bodySm">{channel.referring_channel}</Text>
                  <Text variant="bodySm" tone="subdued">
                    {Number(channel.sessions || 0).toLocaleString("en-US")} sessions
                  </Text>
                </div>
                <Text
                  variant="bodySm"
                  tone={getTrafficSignalTone(getTrafficSignalStatusFromPct(channel.pctChange))}
                >
                  {formatTrafficPercent(channel.pctChange)}
                </Text>
              </div>
            ))}
          </div>
        ) : (
          <Text tone="subdued">No channel data available.</Text>
        )}
      </BlockStack>
    </div>
  );
}
